<template>
  <Teleport key="summary-dialog" to="body">
    <div v-if="show" class="tsd-overlay">
      <div
        class="tsd-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Transaction summary"
        title="Drag the bottom-right corner to resize"
        @click="openMenu = null"
      >
        <div class="tsd-dialog__header">
          <button type="button" class="tsd-back" @click="$emit('close')">
            <i class="bi bi-arrow-left" aria-hidden="true"></i>
            {{ backLabel }}
          </button>
          <div class="tsd-actions">
            <button type="button" class="tsd-btn" @click.stop="$emit('send')">
              <i class="bi bi-send" aria-hidden="true"></i>
              Send
            </button>
            <div class="tsd-menu-wrap">
              <button
                type="button"
                class="tsd-btn"
                aria-label="Print options"
                :aria-expanded="openMenu === 'print'"
                @click.stop="toggleMenu('print')"
              >
                <i class="bi bi-printer" aria-hidden="true"></i>
                <i class="bi bi-chevron-down tsd-btn__caret" aria-hidden="true"></i>
              </button>
              <div v-if="openMenu === 'print'" class="tsd-menu" role="menu">
                <button type="button" class="tsd-menu__item" role="menuitem" @click.stop="printSummary">
                  <i class="bi bi-printer" aria-hidden="true"></i>
                  Print Summary
                </button>
                <button
                  v-if="canDownloadPdf"
                  type="button"
                  class="tsd-menu__item"
                  role="menuitem"
                  @click.stop="selectAction('download-pdf')"
                >
                  <i class="bi bi-download" aria-hidden="true"></i>
                  Download PDF
                </button>
              </div>
            </div>
            <button type="button" class="tsd-dialog__close" aria-label="Close" @click="$emit('close')">
              <i class="bi bi-x-lg" aria-hidden="true"></i>
            </button>
          </div>
        </div>

        <div ref="summary" class="tsd-summary">
          <div class="tsd-summary__top">
            <span class="tsd-summary__title">{{ showPayroll ? payrollHeaderTitle : (showBills ? billHeaderTitle : title) }}</span>
            <span v-if="showPayroll" class="tsd-summary__slot">
              <span :class="['tsd-pill', `tsd-pill--${payrollHeaderTone}`]">{{ payrollHeaderStatus }}</span>
            </span>
            <span v-else-if="showBills" class="tsd-summary__slot">
              <span :class="['tsd-pill', `tsd-pill--${billHeaderTone}`]">{{ billHeaderStatus }}</span>
            </span>
            <span v-show="!showBills && !showPayroll" class="tsd-summary__slot">
              <slot name="badges" />
            </span>
            <span v-if="showPayroll ? payrollHeaderBankRecorded : (showBills ? billHeaderBankRecorded : bankRecorded)" class="rp-bank-linked-chip" :title="showBills ? 'This bill payment is on a recorded bank statement line' : bankRecordedTitle">
              <i class="bi bi-bank2" aria-hidden="true"></i>
              Bank Recorded
            </span>
            <div
              v-if="showPayroll && relatedPayrolls.length > 1"
              class="tsd-payroll-period"
              role="tablist"
              aria-label="Payroll period"
            >
              <span class="tsd-payroll-period__label">Payroll period</span>
              <button
                v-for="payroll in relatedPayrolls"
                :key="payroll.id"
                type="button"
                role="tab"
                class="tsd-payroll-period__tab"
                :class="{ 'tsd-payroll-period__tab--active': selectedPayroll && String(selectedPayroll.id) === String(payroll.id) }"
                :aria-selected="selectedPayroll && String(selectedPayroll.id) === String(payroll.id) ? 'true' : 'false'"
                @click.stop="selectPayrollById(payroll.id)"
              >
                {{ payrollHeaderLabel(payroll) }}
              </button>
            </div>
            <div class="tsd-summary__meta">
              <div class="tsd-summary__meta-main">
                <template v-if="showPayroll">
                  <span v-if="payrollHeaderEmployee">
                    <strong>Consultant:</strong>
                    {{ payrollHeaderEmployee }}
                  </span>
                  <span>
                    <i class="bi bi-calendar3" aria-hidden="true"></i>
                    Pay Period: {{ payrollHeaderPeriod }}
                  </span>
                  <span>
                    <i class="bi bi-calendar3" aria-hidden="true"></i>
                    Check Date: {{ payrollHeaderCheck }}
                  </span>
                </template>
                <template v-else-if="showBills">
                  <span v-if="billHeaderVendor">
                    <strong>Vendor:</strong>
                    {{ billHeaderVendor }}
                  </span>
                  <span>
                    <i class="bi bi-calendar3" aria-hidden="true"></i>
                    Bill Date: {{ billHeaderDate }}
                  </span>
                  <span>
                    <i class="bi bi-calendar3" aria-hidden="true"></i>
                    Due Date: {{ billHeaderDue }}
                  </span>
                </template>
                <template v-else>
                  <span v-if="partyName">
                    <strong>{{ partyLabel }}:</strong>
                    {{ partyName }}
                  </span>
                  <span v-if="dateText">
                    <i class="bi bi-calendar3" aria-hidden="true"></i>
                    {{ dateLabel }}: {{ dateText }}
                  </span>
                  <span v-if="dueDateText">
                    <i class="bi bi-calendar3" aria-hidden="true"></i>
                    Due Date: {{ dueDateText }}
                  </span>
                </template>
              </div>
              <div v-if="headerConsultants.length" class="tsd-summary__consultants">
                <span class="tsd-summary__clabel">{{ headerConsultants.length === 1 ? 'Consultant' : 'Consultants' }}</span>
                <span
                  v-for="(consultant, index) in headerConsultants"
                  :key="`consultant-${consultant.id || index}`"
                  class="tsd-summary__consultant"
                >
                  <span class="tsd-summary__cname">{{ consultant.consultantName || '—' }}</span>
                  <span v-if="consultant.consultantType" class="tsd-summary__ctype">{{ consultant.consultantType }}</span>
                  <span v-if="isC2CType(consultant.consultantType)" class="tsd-summary__cvendor">{{ consultant.vendorName || '—' }}</span>
                </span>
                <button
                  v-if="hasC2CConsultants && invoiceId"
                  type="button"
                  class="tsd-bills-btn"
                  :aria-expanded="showBills ? 'true' : 'false'"
                  @click="toggleBills"
                >
                  <i class="bi bi-receipt-cutoff" aria-hidden="true"></i>
                  {{ showBills ? 'Hide Bills' : 'View Bills' }}
                </button>
                <button
                  v-if="hasPayrollConsultants"
                  type="button"
                  class="tsd-bills-btn"
                  :aria-expanded="showPayroll ? 'true' : 'false'"
                  @click="togglePayroll"
                >
                  <i class="bi bi-wallet2" aria-hidden="true"></i>
                  {{ showPayroll ? 'Hide Payroll' : 'View Payroll' }}
                </button>
              </div>
            </div>
          </div>

          <div class="tsd-summary__cards">
            <div class="tsd-summary__card">
              <i class="bi bi-file-earmark-text tsd-summary__icon tsd-summary__icon--total" aria-hidden="true"></i>
              <div>
                <div class="tsd-summary__label">{{ showPayroll ? 'Gross Pay' : (showBills ? 'Bill Total' : totalLabel) }}</div>
                <div class="tsd-summary__value">{{ showPayroll ? payrollHeaderMoney.gross : (showBills ? billHeaderMoney.total : totalText) }}</div>
              </div>
            </div>
            <div class="tsd-summary__card">
              <i class="bi bi-cash-stack tsd-summary__icon tsd-summary__icon--paid" aria-hidden="true"></i>
              <div>
                <div class="tsd-summary__label">{{ showPayroll ? 'Net Pay' : 'Amount Paid' }}</div>
                <div class="tsd-summary__value">{{ showPayroll ? payrollHeaderMoney.net : (showBills ? billHeaderMoney.paid : paidText) }}</div>
              </div>
            </div>
            <div class="tsd-summary__card">
              <i class="bi bi-bullseye tsd-summary__icon tsd-summary__icon--balance" aria-hidden="true"></i>
              <div>
                <div class="tsd-summary__label">{{ showPayroll ? 'Deductions' : 'Balance Due' }}</div>
                <div class="tsd-summary__value">{{ showPayroll ? payrollHeaderMoney.deductions : (showBills ? billHeaderMoney.balance : balanceText) }}</div>
              </div>
            </div>
            <div class="tsd-summary__card">
              <i class="bi bi-wallet2 tsd-summary__icon tsd-summary__icon--status" aria-hidden="true"></i>
              <div>
                <div class="tsd-summary__label">Status</div>
                <span v-if="showPayroll" class="tsd-summary__slot">
                  <span :class="['tsd-pill', `tsd-pill--${payrollHeaderTone}`]">{{ payrollHeaderStatus }}</span>
                </span>
                <span v-else-if="showBills" class="tsd-summary__slot">
                  <span :class="['tsd-pill', `tsd-pill--${billHeaderTone}`]">{{ billHeaderStatus }}</span>
                </span>
                <span v-show="!showBills && !showPayroll" class="tsd-summary__slot">
                  <slot name="status" />
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showBills && relatedBills.length > 1" class="tsd-bill-picks">
          <button
            v-for="bill in relatedBills"
            :key="bill.id"
            type="button"
            :class="['tsd-bill-picks__btn', { 'tsd-bill-picks__btn--active': selectedBill && selectedBill.id === bill.id }]"
            @click="selectBill(bill)"
          >
            {{ bill.billNo || ('Bill #' + bill.id) }}
          </button>
        </div>

        <ol
          v-if="displayFlow.length"
          :key="summaryViewKey + '-flow'"
          :class="['tsd-flow', { 'tsd-flow--six': displayFlow.length === 6, 'tsd-flow--seven': displayFlow.length >= 7 }]"
          :aria-label="showPayroll ? 'Payroll progress' : (showBills ? 'Bill progress' : 'Invoice progress')"
        >
          <li
            v-for="(step, index) in displayFlow"
            :key="`${summaryViewKey}-${step.key}`"
            :class="['tsd-flow__step', `tsd-flow__step--${step.state}`]"
          >
            <span class="tsd-flow__track">
              <span class="tsd-flow__mark" aria-hidden="true">
                <i :class="step.state === 'pending' ? 'bi bi-plus-lg' : (step.state === 'partial' ? 'bi bi-dash-lg' : 'bi bi-check-lg')"></i>
              </span>
              <span
                v-if="index < displayFlow.length - 1"
                class="tsd-flow__arrow"
                :class="{ 'tsd-flow__arrow--done': displayFlow[index + 1].state !== 'pending' }"
                aria-hidden="true"
              >
                <i class="bi bi-arrow-right"></i>
              </span>
            </span>
            <span class="tsd-flow__label">{{ index + 1 }}. {{ step.label }}</span>
            <span class="tsd-flow__when" :title="step.title || step.when || ''">{{ step.when || (step.state === 'pending' ? 'Pending' : '\u00a0') }}</span>
            <span class="tsd-flow__note" :title="step.title || step.note || ''">{{ step.note || '\u00a0' }}</span>
          </li>
        </ol>

        <div v-if="displayCards.length" :key="summaryViewKey + '-cards'" :class="['tsd-wfcards', { 'tsd-wfcards--bills': showBills, 'tsd-wfcards--payroll': showPayroll }]">
          <article
            v-for="(card, cardIndex) in displayCards"
            :key="`${summaryViewKey}-${card.key}`"
            :class="['tsd-wfcard', { 'tsd-wfcard--active': selectedWorkflowKey === card.key }]"
          >
            <header class="tsd-wfcard__head">
              <span class="tsd-wfcard__num">{{ card.n }}</span>
              <i :class="card.icon" aria-hidden="true"></i>
              <span class="tsd-wfcard__title">{{ card.title }}</span>
            </header>
            <p class="tsd-wfcard__headline" :title="card.headline">{{ card.headline }}</p>
            <ul v-if="card.list" class="tsd-wfcard__list">
              <li v-for="(item, itemIndex) in card.list" :key="`${card.key}-${itemIndex}`">{{ item }}</li>
            </ul>
            <dl v-else class="tsd-wfcard__rows">
              <div v-for="(row, rowIndex) in card.rows" :key="`${card.key}-${rowIndex}`" class="tsd-wfcard__row">
                <dt>{{ row.label }}</dt>
                <dd :title="row.value">
                  <button
                    v-if="row.journalEntry"
                    type="button"
                    class="tsd-journal-link"
                    @click.stop="openJournalEntry(row.journalEntry)"
                  >{{ row.value }}</button>
                  <template v-else>{{ row.value }}</template>
                </dd>
              </div>
            </dl>
            <div class="tsd-wfcard__meta">
              <span :class="['tsd-pill', 'tsd-wfcard__pill', `tsd-pill--${card.tone}`]">{{ card.status }}</span>
            <div class="tsd-wfcard__foot">
              <button
                v-if="card.action"
                type="button"
                class="tsd-wfcard__link"
                @click="openWorkflowCard(card)"
              >
                {{ card.actionLabel }}
                <i class="bi bi-arrow-right" aria-hidden="true"></i>
              </button>
            </div>
            </div>
            <span
              v-if="cardIndex < displayCards.length - 1"
              class="tsd-wfcard__arrow"
              aria-hidden="true"
            >
              <i class="bi bi-arrow-right"></i>
            </span>
          </article>
        </div>

        <section
          v-if="workflowDetail"
          :key="`${summaryViewKey}-${selectedWorkflowKey || 'none'}`"
          ref="workflowDetailPanel"
          class="tsd-wfdetail"
        >
          <header class="tsd-wfdetail__head">
            <span class="tsd-wfdetail__icon" aria-hidden="true">
              <i :class="workflowDetail.icon"></i>
            </span>
            <div class="tsd-wfdetail__heading">
              <div class="tsd-wfdetail__title-row">
                <h3 class="tsd-wfdetail__title">{{ workflowDetail.title }}</h3>
                <span :class="['tsd-pill', `tsd-pill--${workflowDetail.tone}`]">{{ workflowDetail.status }}</span>
              </div>
              <p class="tsd-wfdetail__headline">{{ workflowDetail.headline }}</p>
            </div>
            <button type="button" class="tsd-wfdetail__close" @click="selectedWorkflowKey = null">Close</button>
          </header>
          <div v-if="workflowDetail.stats.length" class="tsd-wfdetail__stats">
            <article v-for="stat in workflowDetail.stats" :key="stat.label" class="tsd-wfdetail__stat">
              <span>{{ stat.label }}</span>
              <strong>{{ stat.value }}</strong>
            </article>
          </div>
          <p v-if="workflowDetail.note" class="tsd-wfdetail__note">{{ workflowDetail.note }}</p>
          <dl v-if="workflowDetail.fields.length" class="tsd-wfdetail__fields">
            <div
              v-for="field in workflowDetail.fields"
              :key="field.label"
              :class="['tsd-wfdetail__field', { 'tsd-wfdetail__field--wide': field.wide }]"
            >
              <dt>{{ field.label }}</dt>
              <dd :title="field.value">
                <button
                  v-if="field.journalEntry"
                  type="button"
                  class="tsd-journal-link"
                  @click="openJournalEntry(field.journalEntry)"
                >{{ field.value }}</button>
                <template v-else>{{ field.value }}</template>
              </dd>
            </div>
          </dl>
          <div v-if="workflowDetail.lines && workflowDetail.lines.length" class="tsd-wfdetail__table-wrap">
            <div class="tsd-wfdetail__table-head">
              <h4 v-if="workflowDetail.tableTitle" class="tsd-wfdetail__table-title">{{ workflowDetail.tableTitle }}</h4>
              <button
                v-if="selectedWorkflowKey === 'customer' && customer && customer.id"
                type="button"
                class="tsd-link-btn"
                @click="viewCustomer"
              >
                View all invoices
                <i class="bi bi-arrow-right" aria-hidden="true"></i>
              </button>
              <button
                v-else-if="selectedWorkflowKey === 'vendor' && billVendor && billVendor.id"
                type="button"
                class="tsd-link-btn"
                @click="viewVendor"
              >
                View all bills
                <i class="bi bi-arrow-right" aria-hidden="true"></i>
              </button>
            </div>
            <table class="tsd-wfdetail__table">
              <thead>
                <tr>
                  <th v-for="column in workflowDetail.columns" :key="column.key">{{ column.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, lineIndex) in workflowDetail.lines" :key="lineIndex">
                  <td v-for="column in workflowDetail.columns" :key="column.key">
                    <button
                      v-if="line.links && line.links[column.key]"
                      type="button"
                      class="tsd-journal-link"
                      @click="openJournalEntry(line.links[column.key])"
                    >{{ line[column.key] }}</button>
                    <template v-else>{{ line[column.key] }}</template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-if="workflowDetail.extraLines && workflowDetail.extraLines.length" class="tsd-wfdetail__table-wrap">
            <div class="tsd-wfdetail__table-head">
              <h4 class="tsd-wfdetail__table-title">{{ workflowDetail.extraTitle }}</h4>
            </div>
            <table class="tsd-wfdetail__table">
              <thead>
                <tr>
                  <th v-for="column in workflowDetail.extraColumns" :key="column.key">{{ column.label }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(line, lineIndex) in workflowDetail.extraLines" :key="`extra-${lineIndex}`">
                  <td v-for="column in workflowDetail.extraColumns" :key="column.key">{{ line[column.key] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else-if="workflowDetail.empty" class="tsd-wfdetail__empty">{{ workflowDetail.empty }}</p>

          <div v-if="workflowDocuments.length" class="tsd-wfdetail__docs">
            <h4 class="tsd-wfdetail__table-title">Documents</h4>
            <ul class="tsd-doc-list">
              <li v-for="doc in workflowDocuments" :key="doc.key" class="tsd-doc">
                <i :class="[doc.icon, 'tsd-doc__icon', `tsd-doc__icon--${doc.tone}`]" aria-hidden="true"></i>
                <span class="tsd-doc__label">
                  {{ doc.label }}
                  <span v-if="doc.sub" class="tsd-doc__sub">{{ doc.sub }}</span>
                </span>
                <button type="button" class="tsd-link-btn" @click="openDocument(doc)">
                  {{ doc.actionLabel }}
                </button>
              </li>
            </ul>
          </div>

          <div
            v-if="selectedWorkflowKey === 'customer' && customer && customer.id && !(workflowDetail.lines && workflowDetail.lines.length)"
            class="tsd-wfdetail__extra"
          >
            <button type="button" class="tsd-link-btn" @click="viewCustomer">
              View all invoices
              <i class="bi bi-arrow-right" aria-hidden="true"></i>
            </button>
          </div>
          <div
            v-if="selectedWorkflowKey === 'vendor' && billVendor && billVendor.id && !(workflowDetail.lines && workflowDetail.lines.length)"
            class="tsd-wfdetail__extra"
          >
            <button type="button" class="tsd-link-btn" @click="viewVendor">
              View all bills
              <i class="bi bi-arrow-right" aria-hidden="true"></i>
            </button>
          </div>

          <div v-if="selectedWorkflowKey === 'timesheet'" class="tsd-wfdetail__table-wrap">
            <div class="tsd-wfdetail__table-head">
              <h4 class="tsd-wfdetail__table-title">
                Timesheets
                <span class="tsd-section__count">{{ timesheetRows.length }}</span>
              </h4>
            </div>
            <table v-if="timesheetRows.length" class="tsd-wfdetail__table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Period</th>
                  <th>Type</th>
                  <th class="tsd-num">Hours</th>
                  <th class="tsd-num">Rate</th>
                  <th class="tsd-num">Amount</th>
                  <th>Status</th>
                  <th>Files</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="(group, groupIndex) in timesheetGroups" :key="group.key || `group-${groupIndex}`">
                  <tr v-if="timesheetGroups.length > 1">
                    <td colspan="8" class="tsd-ts-group__cell">
                      {{ group.consultantName }}
                      <span v-if="group.consultantType" class="tsd-ts-group__type">{{ group.consultantType }}</span>
                      <span class="tsd-ts-group__meta">
                        {{ group.rows.length }} {{ group.rows.length === 1 ? 'timesheet' : 'timesheets' }}
                      </span>
                    </td>
                  </tr>
                  <tr v-for="(row, index) in group.rows" :key="row.key || `row-${groupIndex}-${index}`">
                    <td>{{ index + 1 }}</td>
                    <td>{{ row.period }}</td>
                    <td>{{ row.sourceLabel }}</td>
                    <td class="tsd-num">{{ row.hours }}</td>
                    <td class="tsd-num">{{ row.rate }}</td>
                    <td class="tsd-num">{{ row.amount }}</td>
                    <td>
                      <span class="tsd-pill tsd-pill--neutral">{{ row.status }}</span>
                    </td>
                    <td>
                      <button
                        v-for="(att, attIndex) in row.attachments"
                        :key="att.id || att.filePath || `att-${groupIndex}-${index}-${attIndex}`"
                        type="button"
                        class="tsd-file-btn"
                        :title="att.fileName || att.originalName || 'Attachment'"
                        :aria-label="`Open ${att.fileName || att.originalName || 'attachment'}`"
                        @click="openAttachment(att)"
                      >
                        <i class="bi bi-paperclip" aria-hidden="true"></i>
                      </button>
                      <span v-if="!row.attachments.length">—</span>
                    </td>
                  </tr>
                </template>
                <tr class="tsd-ts-total">
                  <td></td>
                  <td>Total</td>
                  <td></td>
                  <td class="tsd-num">{{ formatHours(timesheetTotals.hours) }}</td>
                  <td></td>
                  <td class="tsd-num">{{ formatCurrencyFn(timesheetTotals.amount) }}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
            <p v-else class="tsd-wfdetail__empty">{{ showPayroll ? 'No timesheets are linked to this payroll period.' : (showBills ? 'No timesheets are linked to this bill.' : 'No timesheets are linked to this invoice.') }}</p>
          </div>
        </section>

        <div v-if="(invoiceId || billId) && detailsLoading" class="tsd-section tsd-section--muted">
          <i class="bi bi-arrow-repeat" aria-hidden="true"></i>
          {{ billId && !invoiceId ? 'Loading bill details…' : 'Loading invoice details…' }}
        </div>
        <div v-else-if="(invoiceId || billId) && detailsError" class="tsd-section tsd-section--error">
          {{ detailsError }}
        </div>
      </div>
    </div>
  </Teleport>

  <JournalEntryDetailsModal
    key="summary-journal"
    :show="!!journalEntryView"
    :journal-entry-id="journalEntryView ? journalEntryView.id : null"
    :entry-number="journalEntryView ? journalEntryView.entryNo : ''"
    @close="journalEntryView = null"
  />
  <ReceivePaymentReceiptPreviewModal
    key="summary-receipt"
    :model-value="!!receiptPreviewGroup"
    :group="receiptPreviewGroup"
    :company-id="companyId"
    @update:model-value="(open) => { if (!open) receiptPreviewGroup = null; }"
  />
</template>

<script>
import '@/utils/bankRecordBadge';
import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';
import JournalEntryDetailsModal from '@/components/JournalEntryDetailsModal.vue';
import ReceivePaymentReceiptPreviewModal from '@/components/ReceivePaymentReceiptPreviewModal.vue';

const defaultCurrency = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const defaultDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default {
  name: 'TransactionSummaryDialog',
  components: { JournalEntryDetailsModal, ReceivePaymentReceiptPreviewModal },
  props: {
    show: { type: Boolean, default: false },
    title: { type: String, default: '' },
    bankRecorded: { type: Boolean, default: false },
    bankRecordedTitle: { type: String, default: '' },
    partyLabel: { type: String, default: 'Customer' },
    partyName: { type: String, default: '' },
    dateLabel: { type: String, default: 'Date' },
    dateText: { type: String, default: '' },
    dueDateText: { type: String, default: '' },
    totalLabel: { type: String, default: 'Total' },
    totalText: { type: String, default: '' },
    paidText: { type: String, default: '' },
    balanceText: { type: String, default: '' },
    backLabel: { type: String, default: 'Back to Transactions' },
    canDownloadPdf: { type: Boolean, default: false },
    /** When set, the popup loads and shows the invoice's customer details and timesheets. */
    invoiceId: { type: [Number, String], default: null },
    /** When set without an invoice, the popup opens on this bill's progress and documents. */
    billId: { type: [Number, String], default: null },
    companyId: { type: [Number, String], default: null },
    formatCurrencyFn: { type: Function, default: defaultCurrency },
    formatDateFn: { type: Function, default: defaultDate }
  },
  emits: ['close', 'send', 'action'],
  data() {
    return {
      openMenu: null,
      invoiceDetails: null,
      recentInvoices: [],
      recentInvoicesLoading: false,
      journalEntryView: null,
      selectedWorkflowKey: null,
      showBills: false,
      selectedBillId: null,
      showPayroll: false,
      selectedPayrollId: null,
      receiptPreviewGroup: null,
      detailsLoading: false,
      detailsError: ''
    };
  },
  computed: {
    detailsLoadKey() {
      if (!this.show || this.invoiceId == null || this.invoiceId === '') return null;
      return `${this.companyId ?? ''}:${this.invoiceId}`;
    },
    billLoadKey() {
      if (!this.show || (this.invoiceId != null && this.invoiceId !== '')) return null;
      if (this.billId == null || this.billId === '') return null;
      return `${this.companyId ?? ''}:bill:${this.billId}`;
    },
    customer() {
      return this.invoiceDetails?.invoice?.customer || null;
    },
    invoiceInfo() {
      return this.invoiceDetails?.invoice || {};
    },
    invoiceMoney() {
      const num = (v) => {
        if (v === null || v === undefined || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
      };
      const inv = this.invoiceInfo;
      const total = num(inv.totalAmount);
      let paid = num(inv.paidAmount);
      let balance = num(inv.balanceAmount);
      if (total != null && balance == null && paid != null) balance = Math.max(0, total - paid);
      if (total != null && paid == null && balance != null) paid = Math.max(0, total - balance);
      return {
        total: total != null ? this.formatCurrencyFn(total) : this.totalText || '—',
        paid: paid != null ? this.formatCurrencyFn(paid) : this.paidText || '—',
        balance: balance != null ? this.formatCurrencyFn(balance) : this.balanceText || '—',
        hasBalance: balance != null ? balance > 0.005 : false
      };
    },
    invoiceFlow() {
      const flow = this.invoiceDetails?.workflow;
      if (!flow) return [];
      const timesheets = this.timesheetRows;
      const timesheetProgress = this.invoiceTimesheetProgress();
      const steps = [
        {
          key: 'timesheet',
          label: 'Timesheet',
          done: timesheets.length > 0 && !timesheetProgress.partial,
          partial: timesheets.length > 0 && timesheetProgress.partial,
          at: timesheetProgress.at,
          note: timesheetProgress.status,
          title: timesheetProgress.status
        },
        { key: 'created', label: 'Invoice Created', done: !!flow.createdAt, at: flow.createdAt },
        {
          key: 'sent',
          label: 'Sent to Customer',
          done: !!flow.sent,
          at: flow.sentAt,
          note: flow.sent ? (flow.emailed ? (flow.sentTo || '') : 'Not emailed') : '',
          title: flow.sentTo || ''
        },
        {
          key: 'paid',
          label: flow.paymentPartial ? 'Partial Payment' : 'Payment Received',
          done: !!flow.paymentReceived && !flow.paymentPartial,
          partial: !!flow.paymentPartial,
          at: flow.paymentAt
        },
        { key: 'reconciled', label: 'Reconciled', done: !!flow.reconciled, at: flow.reconciledAt }
      ];
      let current = -1;
      steps.forEach((step, index) => {
        if (step.done) current = index;
      });
      return steps.map((step, index) => ({
        key: step.key,
        label: step.label,
        when: (step.done || step.partial) ? (step.whenText || this.formatFlowWhen(step.at)) : '',
        note: (step.done || step.partial) ? (step.note || '') : '',
        title: step.title || '',
        state: step.partial
          ? 'partial'
          : (!step.done ? 'pending' : (index === current ? 'current' : 'done'))
      }));
    },
    workflowCards() {
      const details = this.invoiceDetails;
      const flow = details?.workflow;
      if (!details || !flow) return [];
      const inv = this.invoiceInfo;
      const payments = details.payments || [];
      const payment = payments[0] || null;
      const lines = details.journalEntries || [];
      const debitLine = lines.find((line) => Number(line.debit) > 0);
      const debit = lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
      const credit = lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
      const money = this.invoiceMoney;
      const date = (value) => (value ? this.formatDateFn(value) : '—');
      const timesheetProgress = this.invoiceTimesheetProgress();
      const timesheets = this.timesheetRows;
      const timesheetGroups = this.timesheetGroups;
      const timesheetTotals = this.timesheetTotals;
      const firstTimesheet = timesheets[0] || null;
      const timesheetHeadline = timesheetGroups.length === 1
        ? timesheetGroups[0].consultantName
        : (timesheetTotals.count ? `${timesheetTotals.count} timesheets` : 'No timesheets');
      return [
        {
          key: 'timesheet',
          n: 1,
          icon: 'bi bi-clock-history',
          title: 'Timesheet',
          headline: timesheetHeadline,
          rows: [
            { label: 'Type', value: this.summarizeTimesheetSources(timesheets) },
            { label: 'Period', value: firstTimesheet?.period || '—' },
            { label: 'Hours', value: timesheetTotals.count ? this.formatHours(timesheetTotals.hours) : '—' }
          ],
          status: timesheetProgress.status || 'Pending',
          tone: this.timesheetCardTone(timesheetProgress.status),
          action: 'timesheet',
          actionLabel: 'View Details'
        },
        {
          key: 'invoice',
          n: 2,
          icon: 'bi bi-file-earmark-text',
          title: 'Invoice',
          headline: inv.invoiceNo || '—',
          rows: [
            { label: 'Amount', value: money.total },
            { label: 'Date', value: date(inv.date) },
            { label: 'Due', value: date(inv.dueDate) },
            { label: 'Journal', value: lines[0]?.reference || '—', journalEntry: this.journalEntryFromLine(lines[0]) },
            { label: 'Type', value: lines.length ? 'Sales' : '—' },
            { label: 'Posted', value: flow.posted ? date(lines[0]?.date || flow.postedAt) : 'Pending' }
          ],
          status: this.formatStatus(inv.status),
          tone: this.statusTone(inv.status),
          action: 'invoice',
          actionLabel: 'View Details'
        },
        {
          key: 'payment',
          n: 3,
          icon: 'bi bi-receipt',
          title: 'Receipt',
          headline: payment?.reference || '—',
          rows: [
            { label: 'Amount', value: payment ? this.formatCurrencyFn(payment.appliedAmount || payment.amountReceived) : money.paid },
            { label: 'Date', value: payment ? date(payment.paymentDate) : '—' },
            { label: 'Method', value: payment?.paymentMethod || '—' },
            { label: 'Journal', value: (payment?.journalEntries || [])[0]?.reference || '—', journalEntry: this.journalEntryFromLine((payment?.journalEntries || [])[0]) },
            { label: 'Type', value: (payment?.journalEntries || []).length ? 'Receipt' : '—' },
            {
              label: 'Posted',
              value: (payment?.journalEntries || []).some((line) => line.status === 'posted')
                ? date((payment.journalEntries[0].postedAt || payment.journalEntries[0].date))
                : (payment ? 'Pending' : '—')
            }
          ],
          status: !flow.paymentReceived ? 'Pending' : (flow.paymentPartial ? 'Partial' : 'Completed'),
          tone: !flow.paymentReceived ? 'neutral' : (flow.paymentPartial ? 'partial' : 'paid'),
          action: 'payment',
          actionLabel: 'View Details',
          payment
        },
        {
          key: 'bank',
          n: 4,
          icon: 'bi bi-bank',
          title: 'Bank',
          headline: flow.reconciled ? (flow.bank?.via === 'deposit' ? 'Bank deposit' : 'Bank statement') : '—',
          rows: [
            { label: 'Amount', value: flow.bank?.amount != null ? this.formatCurrencyFn(flow.bank.amount) : '—' },
            { label: 'Date', value: date(flow.bank?.date) }
          ],
          status: flow.reconciled ? 'Matched' : 'Pending',
          tone: flow.reconciled ? 'paid' : 'neutral',
          action: 'bank',
          actionLabel: 'View Details'
        },
        {
          key: 'reconciliation',
          n: 5,
          icon: 'bi bi-arrow-left-right',
          title: 'Reconciliation',
          headline: 'Bank reconciliation',
          rows: [
            { label: 'Status', value: flow.reconciled ? 'Matched' : (flow.paymentReceived ? 'Unmatched' : 'Waiting') },
            { label: 'Date', value: date(flow.reconciledAt) }
          ],
          status: flow.reconciled ? 'Matched' : (flow.paymentReceived ? 'In Progress' : 'Pending'),
          tone: flow.reconciled ? 'paid' : (flow.paymentReceived ? 'partial' : 'neutral'),
          action: 'reconciliation',
          actionLabel: 'View Details'
        },
        {
          key: 'gl',
          n: 6,
          icon: 'bi bi-journal-bookmark',
          title: 'General Ledger',
          headline: debitLine?.accountName || '—',
          rows: this.generalLedgerCardRows(lines, debit, credit, (value) => this.formatCurrencyFn(value)),
          status: flow.posted ? 'Posted' : 'Pending',
          tone: flow.posted ? 'sent' : 'neutral',
          action: 'gl',
          actionLabel: 'View Details'
        },
        {
          key: 'reports',
          n: 7,
          icon: 'bi bi-bar-chart',
          title: 'Reports',
          headline: 'Financial reports',
          list: ['P&L', 'Balance Sheet', 'A/R Aging'],
          rows: [],
          status: 'Available',
          tone: 'paid',
          action: 'reports',
          actionLabel: 'View Details'
        }
      ];
    },
    summaryViewKey() {
      if (this.showPayroll) return 'payroll';
      return this.showBills ? 'bill' : 'invoice';
    },
    displayFlow() {
      if (this.showPayroll) return this.payrollFlow;
      return this.showBills ? this.billFlow : this.invoiceFlow;
    },
    displayCards() {
      if (this.showPayroll) return this.payrollCards;
      return this.showBills ? this.billCards : this.workflowCards;
    },
    selectedBill() {
      const bills = this.relatedBills;
      if (!bills.length) return null;
      return bills.find((bill) => bill.id === this.selectedBillId) || bills[0];
    },
    billHeaderTitle() {
      const bill = this.selectedBill;
      if (!bill?.id) return 'Bill';
      return bill.billNo ? `Bill #${bill.billNo}` : `Bill #${bill.id}`;
    },
    billHeaderStatus() {
      return this.selectedBill?.id ? this.formatStatus(this.selectedBill.status) : 'Pending';
    },
    billHeaderTone() {
      return this.selectedBill?.id ? this.statusTone(this.selectedBill.status) : 'neutral';
    },
    billHeaderVendor() {
      return this.selectedBill?.vendorName
        || this.headerConsultants.find((consultant) => this.isC2CType(consultant.consultantType))?.vendorName
        || '';
    },
    billHeaderDate() {
      const date = this.selectedBill?.date;
      return date ? this.formatDateFn(date) : '—';
    },
    billHeaderDue() {
      const date = this.selectedBill?.dueDate;
      return date ? this.formatDateFn(date) : '—';
    },
    billHeaderMoney() {
      const bill = this.selectedBill || {};
      const money = (value) => {
        if (value === null || value === undefined || value === '') return '—';
        const amount = Number(value);
        return Number.isFinite(amount) ? this.formatCurrencyFn(amount) : '—';
      };
      return {
        total: money(bill.total),
        paid: money(bill.paidAmount),
        balance: money(bill.balanceAmount)
      };
    },
    billHeaderBankRecorded() {
      const flow = this.selectedBill?.workflow || {};
      return !!(flow.bank || flow.reconciled);
    },
    billFlow() {
      const flow = this.selectedBill?.workflow || {};
      const hasBill = !!this.selectedBill?.id;
      const created = hasBill && !this.isDraftRecord(this.selectedBill);
      const draftAt = hasBill ? (flow.createdAt || this.selectedBill?.date) : null;
      const timesheets = this.timesheetRows;
      const timesheetProgress = this.billTimesheetProgress();
      const steps = [
        {
          key: 'timesheet',
          label: 'Timesheet',
          done: timesheets.length > 0 && !timesheetProgress.partial,
          partial: timesheets.length > 0 && timesheetProgress.partial,
          at: timesheetProgress.at,
          note: timesheetProgress.status,
          title: timesheetProgress.status
        },
        { key: 'draft', label: 'Draft', done: hasBill, at: draftAt },
        { key: 'created', label: 'Bill Created', done: created, at: created ? draftAt : null },
        {
          key: 'sent',
          label: 'Sent to Vendor',
          done: created && !!flow.sent,
          at: created ? flow.sentAt : null,
          note: created && flow.sent ? 'Not emailed' : '',
          title: flow.sentTo || ''
        },
        {
          key: 'paid',
          label: flow.paymentPartial ? 'Partial Payment' : 'Bill Paid',
          done: created && !!flow.paymentReceived && !flow.paymentPartial,
          partial: created && !!flow.paymentPartial,
          at: created ? flow.paymentAt : null
        },
        { key: 'reconciled', label: 'Reconciled', done: created && !!flow.reconciled, at: created ? flow.reconciledAt : null }
      ];
      let current = -1;
      steps.forEach((step, index) => {
        if (step.done) current = index;
      });
      return steps.map((step, index) => ({
        key: step.key,
        label: step.label,
        when: (step.done || step.partial) ? (step.whenText || this.formatFlowWhen(step.at)) : '',
        note: (step.done || step.partial) ? (step.note || '') : '',
        title: step.title || '',
        state: step.partial
          ? 'partial'
          : (!step.done ? 'pending' : (index === current ? 'current' : 'done'))
      }));
    },
    billCards() {
      const bill = this.selectedBill;
      const flow = bill?.workflow || {};
      const payments = bill?.payments || [];
      const payment = payments[0] || null;
      const date = (value) => (value ? this.formatDateFn(value) : '—');
      const money = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? this.formatCurrencyFn(n) : '—';
      };
      const draft = this.draftBillRecord;
      const posted = this.createdBill;
      const billCreated = !!posted?.id;
      const draftSource = draft || posted;
      const draftStillOpen = !!draft?.id && !billCreated;
      const postedFlow = posted?.workflow || {};
      const postedLines = posted?.journalEntries || [];
      const postedDebit = postedLines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
      const postedCredit = postedLines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
      const timesheets = this.timesheetRows;
      const timesheetGroups = this.timesheetGroups;
      const timesheetTotals = this.timesheetTotals;
      const firstTimesheet = timesheets[0] || null;
      const timesheetProgress = this.billTimesheetProgress();
      const timesheetHeadline = timesheetGroups.length === 1
        ? timesheetGroups[0].consultantName
        : (timesheetTotals.count ? `${timesheetTotals.count} timesheets` : 'No timesheets');
      return [
        {
          key: 'timesheet',
          n: 1,
          icon: 'bi bi-clock-history',
          title: 'Timesheet',
          headline: timesheetHeadline,
          rows: [
            { label: 'Type', value: this.summarizeTimesheetSources(timesheets) },
            { label: 'Period', value: firstTimesheet?.period || '—' },
            { label: 'Hours', value: timesheetTotals.count ? this.formatHours(timesheetTotals.hours) : '—' }
          ],
          status: timesheetProgress.status || 'Pending',
          tone: this.timesheetCardTone(timesheetProgress.status),
          action: 'timesheet',
          actionLabel: 'View Details'
        },
        {
          key: 'draft',
          n: 2,
          icon: 'bi bi-file-earmark-plus',
          title: 'Draft',
          headline: !draftSource?.id ? '—' : (draftStillOpen ? 'Draft' : 'Completed'),
          rows: [
            { label: 'Saved', value: date(draftSource?.workflow?.createdAt || draftSource?.createdAt || draftSource?.date) },
            { label: 'Bill', value: draft?.billNo || posted?.billNo || '—' }
          ],
          status: !draftSource?.id ? 'Pending' : (draftStillOpen ? 'Draft' : 'Completed'),
          tone: !draftSource?.id ? 'neutral' : (draftStillOpen ? 'neutral' : 'paid'),
          action: 'draft',
          actionLabel: 'View Details'
        },
        {
          key: 'bill',
          n: 3,
          icon: 'bi bi-file-earmark-text',
          title: 'Bill',
          headline: billCreated ? (posted.billNo || '—') : '—',
          rows: [
            { label: 'Amount', value: billCreated ? money(posted.total) : '—' },
            { label: 'Date', value: billCreated ? date(posted.date) : '—' },
            { label: 'Due', value: billCreated ? date(posted.dueDate) : '—' },
            { label: 'Journal', value: postedLines[0]?.reference || '—', journalEntry: this.journalEntryFromLine(postedLines[0]) },
            { label: 'Type', value: postedLines.length ? 'Bill' : '—' },
            { label: 'Posted', value: postedFlow.posted ? date(postedLines[0]?.date || postedFlow.postedAt) : (billCreated ? 'Pending' : '—') }
          ],
          status: billCreated ? this.formatStatus(posted.status) : 'Pending',
          tone: billCreated ? this.statusTone(posted.status) : 'neutral',
          action: 'bill',
          actionLabel: 'View Details'
        },
        {
          key: 'payment',
          n: 4,
          icon: 'bi bi-cash-stack',
          title: 'Bill Payment',
          headline: payment?.reference || '—',
          rows: [
            { label: 'Amount', value: payment ? money(payment.appliedAmount) : money(bill?.paidAmount) },
            { label: 'Date', value: payment ? date(payment.paymentDate) : '—' },
            { label: 'Method', value: payment?.paymentMethod || '—' },
            { label: 'Journal', value: (payment?.journalEntries || [])[0]?.reference || '—', journalEntry: this.journalEntryFromLine((payment?.journalEntries || [])[0]) },
            { label: 'Type', value: (payment?.journalEntries || []).length ? 'Bill Payment' : '—' },
            {
              label: 'Posted',
              value: (payment?.journalEntries || []).some((line) => line.status === 'posted')
                ? date(payment.journalEntries[0].postedAt || payment.journalEntries[0].date)
                : (payment ? 'Pending' : '—')
            }
          ],
          status: !flow.paymentReceived ? 'Pending' : (flow.paymentPartial ? 'Partial' : 'Completed'),
          tone: !flow.paymentReceived ? 'neutral' : (flow.paymentPartial ? 'partial' : 'paid'),
          action: 'payment',
          actionLabel: 'View Details'
        },
        {
          key: 'bank',
          n: 5,
          icon: 'bi bi-bank',
          title: 'Bank',
          headline: flow.reconciled ? (flow.bank?.via === 'deposit' ? 'Bank deposit' : 'Bank statement') : '—',
          rows: [
            { label: 'Amount', value: flow.bank?.amount != null ? money(flow.bank.amount) : '—' },
            { label: 'Date', value: date(flow.bank?.date) }
          ],
          status: flow.reconciled ? 'Matched' : 'Pending',
          tone: flow.reconciled ? 'paid' : 'neutral',
          action: 'bank',
          actionLabel: 'View Details'
        },
        {
          key: 'reconciliation',
          n: 6,
          icon: 'bi bi-arrow-left-right',
          title: 'Reconciliation',
          headline: 'Bank reconciliation',
          rows: [
            { label: 'Status', value: flow.reconciled ? 'Matched' : (flow.paymentReceived ? 'Unmatched' : 'Waiting') },
            { label: 'Date', value: date(flow.reconciledAt) }
          ],
          status: flow.reconciled ? 'Matched' : (flow.paymentReceived ? 'In Progress' : 'Pending'),
          tone: flow.reconciled ? 'paid' : (flow.paymentReceived ? 'partial' : 'neutral'),
          action: 'reconciliation',
          actionLabel: 'View Details'
        },
        {
          key: 'gl',
          n: 7,
          icon: 'bi bi-journal-bookmark',
          title: 'General Ledger',
          headline: (postedLines.find((line) => Number(line.debit) > 0)?.accountName) || '—',
          rows: this.generalLedgerCardRows(postedLines, postedDebit, postedCredit, money),
          status: postedFlow.posted ? 'Posted' : 'Pending',
          tone: postedFlow.posted ? 'sent' : 'neutral',
          action: 'gl',
          actionLabel: 'View Details'
        },
        {
          key: 'reports',
          n: 8,
          icon: 'bi bi-bar-chart',
          title: 'Reports',
          headline: 'Financial reports',
          list: ['P&L', 'Balance Sheet', 'A/P Aging'],
          rows: [],
          status: 'Available',
          tone: 'paid',
          action: 'reports',
          actionLabel: 'View Details'
        }
      ];
    },
    billWorkflowDetail() {
      const card = this.billCards.find((item) => item.key === this.selectedWorkflowKey);
      if (!card) return null;
      const bill = this.selectedBill;
      const flow = bill?.workflow || {};
      const when = (value) => (value ? (this.formatFlowWhen(value) || this.formatDateFn(value) || '—') : '—');
      const moneyOf = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? this.formatCurrencyFn(n) : '—';
      };
      const lines = bill?.journalEntries || [];
      const payments = bill?.payments || [];
      const base = {
        n: card.n,
        icon: card.icon,
        title: card.title,
        headline: card.headline,
        status: card.status,
        tone: card.tone,
        note: bill?.id ? '' : 'No bill has been created for this invoice yet.',
        stats: [],
        fields: [],
        columns: [],
        lines: [],
        tableTitle: '',
        empty: ''
      };
      if (card.key === 'timesheet') {
        const groups = this.timesheetGroups;
        const totals = this.timesheetTotals;
        const shown = this.createdBill || bill;
        base.stats = [
          { label: 'Timesheets', value: String(totals.count) },
          { label: 'Hours', value: this.formatHours(totals.hours) },
          { label: 'Amount', value: this.formatCurrencyFn(totals.amount) },
          { label: 'Consultants', value: String(groups.length) }
        ];
        base.fields = [
          { label: 'Type', value: this.summarizeTimesheetSources(this.timesheetRows) },
          { label: 'Bill', value: shown?.billNo || '—' },
          ...(groups.length === 1 && groups[0].vendorName
            ? [{ label: 'Vendor', value: groups[0].vendorName }]
            : [])
        ];
        base.note = totals.count
          ? 'These timesheets are linked to this bill.'
          : '';
      } else if (card.key === 'draft') {
        const draft = this.draftBillRecord;
        const posted = this.createdBill;
        const source = draft || posted;
        const created = !!posted?.id;
        base.note = !source?.id
          ? 'No draft has been saved for this invoice yet.'
          : (created
            ? 'This draft was saved, then the bill number was assigned and the bill was created.'
            : 'This is still a draft. The bill and journal are created after the draft is placed.');
        base.fields = [
          { label: 'Bill number', value: draft?.billNo || posted?.billNo || '—' },
          { label: 'Vendor', value: source?.vendorName || '—' },
          { label: 'Saved', value: when(source?.workflow?.createdAt || source?.createdAt || source?.date) },
          { label: 'Draft status', value: !source?.id ? 'Pending' : (created ? 'Completed' : 'Draft') },
          { label: 'Bill date', value: when(source?.date) },
          { label: 'Due date', value: when(source?.dueDate) },
          { label: 'Amount', value: moneyOf(source?.total) }
        ];
      } else if (card.key === 'vendor') {
        const vendor = this.billVendor;
        const shown = this.createdBill || bill;
        const term = shown?.paymentTerm || vendor?.paymentTerm;
        const recent = Array.isArray(bill?.recentBills) ? bill.recentBills : [];
        base.stats = [
          { label: 'Bill total', value: moneyOf(shown?.total) },
          { label: 'Paid', value: moneyOf(shown?.paidAmount) },
          { label: 'Balance', value: moneyOf(shown?.balanceAmount) },
          { label: 'Other bills', value: String(recent.length) }
        ];
        base.fields = [
          { label: 'Vendor ID', value: vendor?.vendorId || '—' },
          { label: 'Email', value: vendor?.email || '—' },
          { label: 'Phone', value: vendor?.phone || '—' },
          { label: 'Website', value: vendor?.webpage || '—' },
          { label: 'This bill', value: shown?.billNo || '—' },
          { label: 'Bill status', value: shown?.id ? this.formatStatus(shown.status) : '—' },
          { label: 'Bill date', value: when(shown?.date) },
          { label: 'Due date', value: when(shown?.dueDate) },
          { label: 'Payment term', value: term?.name ? `${term.name}${term.dueDays != null ? ` (${term.dueDays} days)` : ''}` : '—' },
          { label: 'Paid', value: moneyOf(shown?.paidAmount) },
          { label: 'Balance', value: moneyOf(shown?.balanceAmount) },
          { label: 'Address', value: this.vendorAddress || '—', wide: true }
        ];
        if (recent.length) {
          base.tableTitle = 'Recent bills';
          base.columns = [
            { key: 'bill', label: 'Bill' },
            { key: 'date', label: 'Date' },
            { key: 'amount', label: 'Amount' },
            { key: 'paid', label: 'Paid' },
            { key: 'balance', label: 'Balance' },
            { key: 'status', label: 'Status' }
          ];
          base.lines = recent.map((row) => ({
            bill: row.billNo || '—',
            date: when(row.date),
            amount: moneyOf(row.total),
            paid: moneyOf(row.paidAmount),
            balance: moneyOf(row.balanceAmount),
            status: this.formatStatus(row.status)
          }));
        }
      } else if (card.key === 'bill') {
        const posted = this.createdBill;
        const postedFlow = posted?.workflow || {};
        if (!posted?.id) {
          base.note = 'The draft is not the created bill. Bill details appear here after the draft is placed and the bill is created.';
        } else {
          base.stats = [
            { label: 'Bill total', value: moneyOf(posted.total) },
            { label: 'Paid', value: moneyOf(posted.paidAmount) },
            { label: 'Balance', value: moneyOf(posted.balanceAmount) },
            { label: 'Payments', value: String((posted.payments || []).length) }
          ];
          base.fields = [
            { label: 'Vendor', value: posted.vendorName || '—' },
            { label: 'Bill number', value: posted.billNo || '—' },
            { label: 'Status', value: this.formatStatus(posted.status) },
            { label: 'Bill date', value: when(posted.date) },
            { label: 'Due date', value: when(posted.dueDate) },
            { label: 'Created', value: when(postedFlow.createdAt) },
            { label: 'Posted', value: postedFlow.posted ? when(postedFlow.postedAt) : 'Not yet' },
            { label: 'Paid', value: postedFlow.paymentReceived ? when(postedFlow.paymentAt) : 'Not yet' }
          ];
          const journalLines = posted.journalEntries || [];
          const debit = journalLines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
          const credit = journalLines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
          base.fields.push(
            { label: 'Journal entry', value: journalLines[0]?.reference || '—', journalEntry: this.journalEntryFromLine(journalLines[0]) },
            { label: 'Journal date', value: when(journalLines[0]?.date) },
            { label: 'Type', value: journalLines.length ? 'Bill' : '—' }
          );
          base.stats.push(
            { label: 'Debit', value: journalLines.length ? moneyOf(debit) : '—' },
            { label: 'Credit', value: journalLines.length ? moneyOf(credit) : '—' }
          );
          base.tableTitle = 'Journal lines';
          base.columns = [
            { key: 'account', label: 'Account' },
            { key: 'description', label: 'Description' },
            { key: 'debit', label: 'Debit' },
            { key: 'credit', label: 'Credit' }
          ];
          base.lines = journalLines.map((line) => ({
            account: line.accountName || '—',
            description: line.description || '—',
            debit: Number(line.debit) ? moneyOf(line.debit) : '—',
            credit: Number(line.credit) ? moneyOf(line.credit) : '—'
          }));
          base.empty = journalLines.length ? '' : 'No journal entry is posted for this bill.';
        }
      } else if (card.key === 'gl') {
        const posted = this.createdBill;
        const postedFlow = posted?.workflow || {};
        const postedLines = posted?.journalEntries || [];
        base.note = postedFlow.posted
          ? 'These journal entries post the created bill to the general ledger.'
          : 'No journal entry is linked to the created bill yet.';
        base.tableTitle = 'Journal lines';
        base.columns = [
          { key: 'journal', label: 'Journal' },
          { key: 'account', label: 'Account' },
          { key: 'description', label: 'Description' },
          { key: 'debit', label: 'Debit' },
          { key: 'credit', label: 'Credit' }
        ];
        base.lines = postedLines.map((line) => ({
          journal: line.reference || '—',
          account: line.accountName || '—',
          description: line.description || '—',
          debit: Number(line.debit) ? moneyOf(line.debit) : '—',
          credit: Number(line.credit) ? moneyOf(line.credit) : '—',
          links: { journal: this.journalEntryFromLine(line) }
        }));
        base.empty = lines.length ? '' : 'No journal lines for this bill.';
      } else if (card.key === 'payment') {
        base.note = payments.length
          ? `${payments.length} bill payment${payments.length === 1 ? '' : 's'} applied.`
          : '';
        base.empty = payments.length ? '' : 'No bill payment has been recorded.';
        base.tableTitle = 'Bill payments';
        base.columns = [
          { key: 'reference', label: 'Reference' },
          { key: 'date', label: 'Date' },
          { key: 'method', label: 'Method' },
          { key: 'applied', label: 'Amount' },
          { key: 'journal', label: 'Journal' }
        ];
        base.lines = payments.map((payment) => ({
          reference: payment.reference || '—',
          date: when(payment.paymentDate),
          method: payment.paymentMethod || '—',
          applied: moneyOf(payment.appliedAmount),
          journal: (payment.journalEntries || [])[0]?.reference || '—',
          links: {
            journal: this.journalEntryFromLine((payment.journalEntries || [])[0])
          }
        }));
        const payJournalLines = payments.flatMap((payment) => payment.journalEntries || []);
        const payDebit = payJournalLines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
        const payCredit = payJournalLines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
        const payPosted = payJournalLines.some((line) => line.status === 'posted');
        base.fields = [
          { label: 'Journal entry', value: payJournalLines[0]?.reference || '—', journalEntry: this.journalEntryFromLine(payJournalLines[0]) },
          { label: 'Journal date', value: when(payJournalLines[0]?.date) },
          { label: 'Posted', value: payPosted ? when(payJournalLines[0]?.postedAt || payJournalLines[0]?.date) : 'Not yet' },
          { label: 'Type', value: payJournalLines.length ? 'Bill Payment' : '—' }
        ];
        base.stats = [
          { label: 'Debit', value: payJournalLines.length ? moneyOf(payDebit) : '—' },
          { label: 'Credit', value: payJournalLines.length ? moneyOf(payCredit) : '—' }
        ];
        base.extraTitle = 'Journal lines';
        base.extraColumns = [
          { key: 'account', label: 'Account' },
          { key: 'description', label: 'Description' },
          { key: 'debit', label: 'Debit' },
          { key: 'credit', label: 'Credit' }
        ];
        base.extraLines = payJournalLines.map((line) => ({
          account: line.accountName || '—',
          description: line.description || '—',
          debit: Number(line.debit) ? moneyOf(line.debit) : '—',
          credit: Number(line.credit) ? moneyOf(line.credit) : '—'
        }));
      } else if (card.key === 'bank' || card.key === 'reconciliation') {
        base.note = flow.reconciled
          ? 'The bill payment is matched to a recorded bank line.'
          : 'No bank statement line is matched to this bill yet.';
        base.fields = [
          { label: 'Bank amount', value: flow.bank?.amount != null ? moneyOf(flow.bank.amount) : '—' },
          { label: 'Bank date', value: when(flow.bank?.date || flow.reconciledAt) },
          { label: 'Match', value: flow.reconciled ? 'Matched' : 'Pending' },
          { label: 'Bill paid', value: moneyOf(bill?.paidAmount) }
        ];
      } else if (card.key === 'reports') {
        base.note = 'This bill is included in profit and loss, the balance sheet, and accounts payable aging.';
        base.fields = [
          { label: 'Profit and loss', value: 'Expense from the bill' },
          { label: 'Balance sheet', value: flow.paymentReceived && !flow.paymentPartial ? 'Paid' : 'Accounts payable' },
          { label: 'A/P aging', value: flow.paymentPartial || !flow.paymentReceived ? 'Open balance' : 'Cleared' }
        ];
      }
      return base;
    },
    payrollWorkflowDetail() {
      const card = this.payrollCards.find((item) => item.key === this.selectedWorkflowKey);
      if (!card) return null;
      const payroll = this.selectedPayroll;
      const flow = payroll?.workflow || {};
      const when = (value) => (value ? (this.formatFlowWhen(value) || this.formatDateFn(value) || '—') : '—');
      const moneyOf = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? this.formatCurrencyFn(n) : '—';
      };
      const journalLines = payroll?.journalEntries || [];
      const base = {
        n: card.n,
        icon: card.icon,
        title: card.title,
        headline: card.headline,
        status: card.status,
        tone: card.tone,
        note: payroll?.id ? '' : 'No payroll has been processed for this consultant yet.',
        stats: [],
        fields: [],
        columns: [],
        lines: [],
        tableTitle: '',
        empty: ''
      };
      if (card.key === 'timesheet') {
        const totals = this.timesheetTotals;
        base.stats = [
          { label: 'Timesheets', value: String(totals.count) },
          { label: 'Hours', value: this.formatHours(totals.hours) },
          { label: 'Amount', value: this.formatCurrencyFn(totals.amount) },
          { label: 'Gross pay', value: moneyOf(payroll?.gross) }
        ];
        base.fields = [
          { label: 'Type', value: this.summarizeTimesheetSources(this.timesheetRows) },
          { label: 'Pay period', value: this.payrollHeaderPeriod },
          { label: 'Consultant', value: this.payrollHeaderEmployee || '—' }
        ];
        base.note = totals.count ? 'These timesheets fall in this payroll period.' : '';
      } else if (card.key === 'processed') {
        base.stats = [
          { label: 'Gross pay', value: moneyOf(payroll?.gross) },
          { label: 'Deductions', value: moneyOf(payroll?.deductions) },
          { label: 'Net pay', value: moneyOf(payroll?.net) },
          { label: 'People', value: String((payroll?.lines || []).length) }
        ];
        base.fields = [
          { label: 'Payroll', value: payroll?.id ? `Payroll #${payroll.id}` : '—' },
          { label: 'Status', value: payroll?.id ? this.formatStatus(payroll.status) : '—' },
          { label: 'Pay period', value: this.payrollHeaderPeriod },
          { label: 'Processed', value: when(flow.createdAt) },
          { label: 'Check date', value: when(flow.checkAt) }
        ];
        const detailRows = this.payrollDetailRows(payroll);
        const detailTotal = (key) => (payroll?.lines || []).reduce((sum, line) => sum + (Number(line[key]) || 0), 0);
        base.stats.push(
          { label: 'Earnings', value: moneyOf(detailTotal('earnings')) },
          { label: 'Reimbursement', value: moneyOf(detailTotal('reimbursement')) },
          { label: 'Benefits', value: moneyOf(detailTotal('benefits')) }
        );
        base.note = 'Earnings, reimbursement, benefits, taxes, and other payroll amounts for this pay period.';
        base.tableTitle = 'Pay lines';
        base.columns = [
          { key: 'name', label: 'Consultant' },
          { key: 'gross', label: 'Gross' },
          { key: 'deductions', label: 'Deductions' },
          { key: 'net', label: 'Net' }
        ];
        base.lines = (payroll?.lines || []).map((line) => ({
          name: line.employeeName || '—',
          gross: moneyOf(line.grossPay),
          deductions: moneyOf(line.deductions),
          net: moneyOf(line.netPay)
        }));
        base.extraTitle = 'Payroll details';
        base.extraColumns = [
          { key: 'name', label: 'Consultant' },
          { key: 'item', label: 'Detail' },
          { key: 'amount', label: 'Amount' }
        ];
        base.extraLines = detailRows;
        base.empty = base.lines.length ? '' : 'No pay lines for this consultant.';
      } else if (card.key === 'paycheck') {
        base.note = flow.checkIssued
          ? 'This paycheck is the net pay for the consultant on this payroll.'
          : 'No check date is recorded for this payroll yet.';
        base.fields = [
          { label: 'Consultant', value: this.payrollHeaderEmployee || '—' },
          { label: 'Net pay', value: moneyOf(payroll?.net) },
          { label: 'Check date', value: when(flow.checkAt) },
          { label: 'Pay date', value: when(payroll?.payDate) }
        ];
      } else if (card.key === 'gl') {
        const journalGroups = this.payrollJournalGroups(journalLines);
        const debit = journalLines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
        const credit = journalLines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
        base.note = journalLines.length
          ? 'These journal entries record wages, payroll taxes, and reimbursements for this payroll.'
          : 'No journal entry is linked to this payroll yet.';
        base.stats = [
          { label: 'Journals', value: String(journalGroups.length) },
          { label: 'Debit', value: journalLines.length ? moneyOf(debit) : '—' },
          { label: 'Credit', value: journalLines.length ? moneyOf(credit) : '—' }
        ];
        base.tableTitle = 'Journal lines';
        base.columns = [
          { key: 'journal', label: 'Journal' },
          { key: 'type', label: 'Type' },
          { key: 'account', label: 'Account' },
          { key: 'description', label: 'Description' },
          { key: 'debit', label: 'Debit' },
          { key: 'credit', label: 'Credit' }
        ];
        base.lines = journalLines.map((line) => ({
          journal: line.reference || '—',
          type: line.title || 'Journal entry',
          account: line.accountName || '—',
          description: line.description || '—',
          debit: Number(line.debit) ? moneyOf(line.debit) : '—',
          credit: Number(line.credit) ? moneyOf(line.credit) : '—',
          links: { journal: this.journalEntryFromLine(line) }
        }));
        base.empty = journalLines.length ? '' : 'No journal lines for this payroll.';
      } else if (card.key === 'bank') {
        base.note = flow.reconciled
          ? 'This paycheck is matched to a bank statement line.'
          : 'This paycheck is not matched to a bank statement line yet.';
        base.fields = [
          { label: 'Status', value: flow.reconciled ? 'Matched' : 'Pending' },
          { label: 'Date', value: when(flow.reconciledAt) },
          { label: 'Amount', value: flow.bank?.amount != null ? moneyOf(flow.bank.amount) : moneyOf(payroll?.net) }
        ];
      } else if (card.key === 'reports') {
        base.note = 'This payroll is included in pay slips, the payroll summary, and profit and loss.';
        base.fields = [
          { label: 'Pay slips', value: this.payrollHeaderEmployee || 'Consultant' },
          { label: 'Payroll summary', value: this.payrollHeaderPeriod },
          { label: 'Profit and loss', value: 'Wages and payroll taxes' }
        ];
      }
      return base;
    },
    workflowDetail() {
      if (this.showPayroll) return this.payrollWorkflowDetail;
      if (this.showBills) return this.billWorkflowDetail;
      const card = this.workflowCards.find((item) => item.key === this.selectedWorkflowKey);
      const details = this.invoiceDetails;
      if (!card || !details) return null;
      const flow = details.workflow || {};
      const inv = this.invoiceInfo;
      const customer = this.customer;
      const money = this.invoiceMoney;
      const when = (value) => (value ? (this.formatFlowWhen(value) || this.formatDateFn(value) || '—') : '—');
      const moneyOf = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? this.formatCurrencyFn(n) : '—';
      };
      const base = {
        n: card.n,
        icon: card.icon,
        title: card.title,
        headline: card.headline,
        status: card.status,
        tone: card.tone,
        note: '',
        stats: [],
        fields: [],
        columns: [],
        lines: [],
        tableTitle: '',
        empty: ''
      };
      if (card.key === 'timesheet') {
        const groups = this.timesheetGroups;
        const totals = this.timesheetTotals;
        base.stats = [
          { label: 'Timesheets', value: String(totals.count) },
          { label: 'Hours', value: this.formatHours(totals.hours) },
          { label: 'Amount', value: this.formatCurrencyFn(totals.amount) },
          { label: 'Consultants', value: String(groups.length) }
        ];
        base.fields = [
          { label: 'Type', value: this.summarizeTimesheetSources(this.timesheetRows) },
          { label: 'Invoice', value: inv.invoiceNo || '—' },
          ...(groups.length === 1 && groups[0].vendorName
            ? [{ label: 'Vendor', value: groups[0].vendorName }]
            : [])
        ];
        base.note = totals.count
          ? 'These timesheets are linked to this invoice.'
          : '';
      } else if (card.key === 'invoice') {
        base.stats = [
          { label: 'Total', value: money.total },
          { label: 'Paid', value: money.paid },
          { label: 'Balance', value: money.balance },
          { label: 'Timesheet hours', value: this.formatHours(this.timesheetTotals.hours) }
        ];
        base.fields = [
          { label: 'Invoice number', value: inv.invoiceNo || '—' },
          { label: 'Customer', value: customer?.customerName || inv.customerName || '—' },
          { label: 'Invoice date', value: when(inv.date) },
          { label: 'Due date', value: when(inv.dueDate) },
          { label: 'Payment term', value: inv.paymentTerm ? `${inv.paymentTerm.name}${inv.paymentTerm.dueDays != null ? ` (${inv.paymentTerm.dueDays} days)` : ''}` : '—' },
          { label: 'Status', value: this.formatStatus(inv.status) },
          { label: 'Created', value: when(flow.createdAt || inv.createdAt) },
          { label: 'Last updated', value: when(inv.updatedAt) },
          { label: 'Sent to', value: flow.sentTo || '—' },
          { label: 'Email', value: flow.emailed ? `Emailed ${when(flow.sentAt)}` : 'Saved, not emailed' },
          { label: 'Subtotal', value: moneyOf(inv.subtotal) },
          { label: 'Tax', value: moneyOf(inv.taxAmount) },
          { label: 'Bonus', value: moneyOf(inv.bonusAmount) },
          { label: 'Discount', value: moneyOf(inv.discountAmount) }
        ];
        const journalLines = details.journalEntries || [];
        const debit = journalLines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
        const credit = journalLines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
        base.fields.push(
          { label: 'Journal entry', value: journalLines[0]?.reference || '—', journalEntry: this.journalEntryFromLine(journalLines[0]) },
          { label: 'Journal date', value: when(journalLines[0]?.date) },
          { label: 'Posted', value: flow.posted ? when(flow.postedAt) : 'Not yet' }
        );
        base.tableTitle = 'Journal lines';
        base.columns = [
          { key: 'account', label: 'Account' },
          { key: 'description', label: 'Description' },
          { key: 'debit', label: 'Debit' },
          { key: 'credit', label: 'Credit' }
        ];
        base.lines = journalLines.map((line) => ({
          account: line.accountName || '—',
          description: line.description || '—',
          debit: Number(line.debit) ? moneyOf(line.debit) : '—',
          credit: Number(line.credit) ? moneyOf(line.credit) : '—'
        }));
        base.empty = journalLines.length ? '' : 'No journal entry is posted for this invoice.';
        base.stats.push(
          { label: 'Debit', value: journalLines.length ? moneyOf(debit) : '—' },
          { label: 'Credit', value: journalLines.length ? moneyOf(credit) : '—' }
        );
      } else if (card.key === 'payment') {
        const payments = details.payments || [];
        const applied = payments.reduce((sum, payment) => sum + (Number(payment.appliedAmount) || 0), 0);
        base.note = payments.length
          ? `${payments.length} receipt${payments.length === 1 ? '' : 's'} applied to this invoice.`
          : '';
        base.empty = payments.length ? '' : 'No payment has been recorded for this invoice.';
        base.stats = [
          { label: 'Paid', value: money.paid },
          { label: 'Applied', value: payments.length ? moneyOf(applied) : '—' },
          { label: 'Balance', value: money.balance },
          { label: 'Receipts', value: String(payments.length) }
        ];
        base.tableTitle = 'Receipts';
        base.columns = [
          { key: 'reference', label: 'Reference' },
          { key: 'date', label: 'Date' },
          { key: 'method', label: 'Method' },
          { key: 'received', label: 'Received' },
          { key: 'applied', label: 'Applied to invoice' },
          { key: 'discount', label: 'Discount' },
          { key: 'writeOff', label: 'Write-off' },
          { key: 'journal', label: 'Journal' }
        ];
        base.lines = payments.map((payment) => {
          const records = payment.records || [];
          const sumOf = (key) => records.reduce((sum, record) => sum + (Number(record[key]) || 0), 0);
          return {
            reference: payment.reference || '—',
            date: when(payment.paymentDate),
            method: payment.paymentMethod || '—',
            received: moneyOf(payment.amountReceived),
            applied: moneyOf(payment.appliedAmount),
            discount: moneyOf(sumOf('discountAmount')),
            writeOff: moneyOf(sumOf('writeOff')),
            journal: (payment.journalEntries || [])[0]?.reference || '—',
            links: {
              journal: this.journalEntryFromLine((payment.journalEntries || [])[0])
            }
          };
        });
        const receiptJournalLines = payments.flatMap((payment) => payment.journalEntries || []);
        const receiptDebit = receiptJournalLines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
        const receiptCredit = receiptJournalLines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
        const receiptPosted = receiptJournalLines.some((line) => line.status === 'posted');
        base.fields = [
          { label: 'Journal entry', value: receiptJournalLines[0]?.reference || '—', journalEntry: this.journalEntryFromLine(receiptJournalLines[0]) },
          { label: 'Journal date', value: when(receiptJournalLines[0]?.date) },
          { label: 'Posted', value: receiptPosted ? when(receiptJournalLines[0]?.postedAt || receiptJournalLines[0]?.date) : 'Not yet' },
          { label: 'Type', value: receiptJournalLines.length ? 'Receipt' : '—' }
        ];
        base.stats.push(
          { label: 'Debit', value: receiptJournalLines.length ? moneyOf(receiptDebit) : '—' },
          { label: 'Credit', value: receiptJournalLines.length ? moneyOf(receiptCredit) : '—' }
        );
        base.extraTitle = 'Journal lines';
        base.extraColumns = [
          { key: 'account', label: 'Account' },
          { key: 'description', label: 'Description' },
          { key: 'debit', label: 'Debit' },
          { key: 'credit', label: 'Credit' }
        ];
        base.extraLines = receiptJournalLines.map((line) => ({
          account: line.accountName || '—',
          description: line.description || '—',
          debit: Number(line.debit) ? moneyOf(line.debit) : '—',
          credit: Number(line.credit) ? moneyOf(line.credit) : '—'
        }));
      } else if (card.key === 'bank') {
        base.note = flow.reconciled
          ? 'The customer receipt is matched to a recorded bank line.'
          : 'No bank statement line is matched to this invoice yet.';
        base.stats = [
          { label: 'Bank amount', value: flow.bank?.amount != null ? moneyOf(flow.bank.amount) : '—' },
          { label: 'Invoice paid', value: money.paid },
          { label: 'Match', value: flow.reconciled ? 'Matched' : 'Pending' }
        ];
        base.fields = [
          { label: 'Source', value: flow.bank?.via === 'deposit' ? 'Bank deposit' : (flow.reconciled ? 'Direct bank statement' : 'Not matched') },
          { label: 'Bank date', value: when(flow.bank?.date) },
          { label: 'Invoice', value: inv.invoiceNo || '—' },
          { label: 'Customer', value: customer?.customerName || inv.customerName || '—' },
          { label: 'Payment date', value: when(flow.paymentAt) },
          { label: 'Status', value: card.status }
        ];
      } else if (card.key === 'reconciliation') {
        base.note = flow.reconciled
          ? 'Reconciled means the receipt is matched to a bank statement line, directly or through a deposit.'
          : 'This step stays open until the receipt is matched to a recorded bank statement line.';
        base.stats = [
          { label: 'Match', value: flow.reconciled ? 'Matched' : (flow.paymentReceived ? 'Unmatched' : 'Waiting') },
          { label: 'Paid', value: money.paid },
          { label: 'Balance', value: money.balance }
        ];
        base.fields = [
          { label: 'Status', value: card.status },
          { label: 'Reconciled on', value: when(flow.reconciledAt) },
          { label: 'Matched through', value: flow.bank?.via === 'deposit' ? 'Bank deposit' : (flow.reconciled ? 'Bank statement' : '—') },
          { label: 'Bank amount', value: flow.bank?.amount != null ? moneyOf(flow.bank.amount) : '—' },
          { label: 'Payment received', value: flow.paymentReceived ? when(flow.paymentAt) : 'Not yet' },
          { label: 'Invoice', value: inv.invoiceNo || '—' }
        ];
      } else if (card.key === 'journal' || card.key === 'gl') {
        const lines = details.journalEntries || [];
        const debit = lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
        const credit = lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
        base.note = card.key === 'gl'
          ? 'These ledger lines were posted when the invoice was saved.'
          : 'This sales journal entry was created when the invoice was saved.';
        base.empty = lines.length ? '' : 'No journal entry is posted for this invoice.';
        base.stats = [
          { label: 'Debit', value: lines.length ? moneyOf(debit) : '—' },
          { label: 'Credit', value: lines.length ? moneyOf(credit) : '—' },
          { label: 'Lines', value: String(lines.length) },
          { label: 'Posted', value: flow.posted ? 'Yes' : 'No' }
        ];
        const journalGroups = this.payrollJournalGroups(lines);
        base.fields = [
          ...(journalGroups.length
            ? journalGroups.map((group, index) => ({
              label: journalGroups.length > 1 ? `Journal ${index + 1}` : 'Entry number',
              value: group.reference || '—',
              journalEntry: group.journalEntry
            }))
            : [{ label: 'Entry number', value: '—' }]),
          { label: 'Entry date', value: when(lines[0]?.date) },
          { label: 'Posted', value: when(flow.postedAt) },
          { label: 'Type', value: lines.length ? 'Sales' : '—' },
          { label: 'Invoice reference', value: inv.invoiceNo || '—' },
          { label: 'Customer', value: customer?.customerName || inv.customerName || '—' }
        ];
        base.tableTitle = card.key === 'gl' ? 'Ledger lines' : 'Journal lines';
        base.columns = [
          { key: 'journal', label: 'Journal' },
          { key: 'account', label: 'Account' },
          { key: 'description', label: 'Description' },
          { key: 'debit', label: 'Debit' },
          { key: 'credit', label: 'Credit' }
        ];
        base.lines = lines.map((line) => ({
          journal: line.reference || '—',
          account: line.accountName || '—',
          description: line.description || '—',
          debit: Number(line.debit) ? moneyOf(line.debit) : '—',
          credit: Number(line.credit) ? moneyOf(line.credit) : '—',
          links: { journal: this.journalEntryFromLine(line) }
        }));
      } else if (card.key === 'reports') {
        base.note = 'Figures below are from this invoice only.';
        base.stats = [
          { label: 'Income', value: money.total },
          { label: 'Open balance', value: money.balance },
          { label: 'Due', value: when(inv.dueDate) }
        ];
        base.fields = [
          { label: 'Profit & Loss', value: `Sales income of ${money.total} from ${inv.invoiceNo || 'this invoice'}.`, wide: true },
          { label: 'Balance Sheet', value: money.hasBalance ? `Accounts receivable still open for ${money.balance}.` : 'No open receivable. The invoice is fully paid.', wide: true },
          { label: 'A/R Aging', value: money.hasBalance ? `Balance ${money.balance} is aged from the due date ${when(inv.dueDate)}.` : 'Nothing left to age. The balance is zero.', wide: true }
        ];
      }
      return base;
    },
    relatedDocuments() {
      const details = this.invoiceDetails;
      if (!details) return [];
      const docs = [];

      for (const att of details.attachments || []) {
        const name = att.fileName || String(att.filePath || '').split('/').pop() || 'Attachment';
        const isPdf = att.mimeType === 'application/pdf' || /\.pdf$/i.test(name);
        const isInvoicePdf = isPdf && (att.source === 'invoice' || !att.source);
        docs.push({
          key: `att-${att.id}`,
          kind: 'attachment',
          label: isInvoicePdf ? 'Invoice PDF' : name,
          sub: isInvoicePdf ? name : '',
          icon: isPdf ? 'bi bi-file-earmark-pdf' : 'bi bi-file-earmark',
          tone: isPdf ? 'pdf' : 'file',
          actionLabel: 'Download',
          attachment: att
        });
      }

      for (const payment of details.payments || []) {
        docs.push({
          key: `pay-${payment.id}`,
          kind: 'receipt',
          label: 'Payment Receipt',
          sub: [payment.reference, this.formatDateFn(payment.paymentDate)].filter(Boolean).join(' · '),
          icon: 'bi bi-receipt',
          tone: 'receipt',
          actionLabel: 'Download',
          payment
        });
      }

      const seenJe = new Set();
      for (const line of details.journalEntries || []) {
        const id = line.journalEntryId;
        if (id == null || seenJe.has(id)) continue;
        seenJe.add(id);
        docs.push({
          key: `je-${id}`,
          kind: 'journal',
          label: 'Journal Entry',
          sub: line.reference || '',
          icon: 'bi bi-journal-text',
          tone: 'journal',
          actionLabel: 'View',
          journalEntry: { id, entryNo: line.reference || '' }
        });
      }
      for (const payment of details.payments || []) {
        for (const line of payment.journalEntries || []) {
          const id = line.journalEntryId;
          if (id == null || seenJe.has(id)) continue;
          seenJe.add(id);
          docs.push({
            key: `je-pay-${id}`,
            kind: 'journal',
            paymentId: payment.id,
            label: 'Journal Entry',
            sub: line.reference || '',
            icon: 'bi bi-journal-text',
            tone: 'journal',
            actionLabel: 'View',
            journalEntry: { id, entryNo: line.reference || '' }
          });
        }
      }
      return docs;
    },
    workflowDocuments() {
      const key = this.selectedWorkflowKey;
      if (!key) return [];
      return this.relatedDocuments.filter((doc) => {
        if (key === 'invoice') return doc.kind === 'attachment' || doc.kind === 'journal';
        if (key === 'payment') return doc.kind === 'receipt' || (doc.kind === 'journal' && doc.paymentId != null);
        if (key === 'journal' || key === 'gl') return doc.kind === 'journal';
        return false;
      });
    },
    customerAddress() {
      const c = this.customer;
      if (!c) return '';
      return [c.address, c.addressDetails]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(', ');
    },
    billVendor() {
      const vendor = this.selectedBill?.vendor;
      return vendor && typeof vendor === 'object' ? vendor : null;
    },
    vendorAddress() {
      const vendor = this.billVendor;
      if (!vendor) return '';
      return [vendor.address, vendor.addressDetails]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(', ');
    },
    headerConsultants() {
      const consultants = this.invoiceDetails?.consultants;
      if (!Array.isArray(consultants)) return [];
      return consultants.filter((consultant) => consultant && typeof consultant === 'object');
    },
    hasC2CConsultants() {
      return this.headerConsultants.some((consultant) => this.isC2CType(consultant.consultantType));
    },
    relatedBills() {
      return Array.isArray(this.invoiceDetails?.bills) ? this.invoiceDetails.bills : [];
    },
    relatedPayrolls() {
      const payrolls = Array.isArray(this.invoiceDetails?.payrolls) ? this.invoiceDetails.payrolls : [];
      return payrolls
        .filter((payroll) => payroll.matchesTimesheet !== false)
        .slice()
        .sort((a, b) => this.payrollPeriodSortKey(a).localeCompare(this.payrollPeriodSortKey(b)));
    },
    hasPayroll() {
      return this.relatedPayrolls.length > 0;
    },
    hasPayrollConsultants() {
      return this.headerConsultants.some((consultant) => this.isPayrollType(consultant.consultantType));
    },
    selectedPayroll() {
      const payrolls = this.relatedPayrolls;
      if (!payrolls.length) return null;
      return payrolls.find((payroll) => String(payroll.id) === String(this.selectedPayrollId)) || payrolls[0];
    },
    payrollConsultantIds() {
      const ids = new Set(
        (this.selectedPayroll?.lines || [])
          .map((line) => String(line.consultantId ?? ''))
          .filter(Boolean)
      );
      return ids;
    },
    payrollHeaderTitle() {
      const payroll = this.selectedPayroll;
      if (!payroll?.id) return 'Payroll';
      if (payroll.staged) return 'Staged payroll';
      return `Payroll #${payroll.id}`;
    },
    payrollHeaderStatus() {
      return this.selectedPayroll?.id ? this.formatStatus(this.selectedPayroll.status) : 'Pending';
    },
    payrollHeaderTone() {
      const status = String(this.selectedPayroll?.status || '').toLowerCase();
      if (!this.selectedPayroll?.id) return 'neutral';
      if (status === 'draft') return 'neutral';
      if (status === 'staged') return 'sent';
      return 'paid';
    },
    payrollHeaderEmployee() {
      const names = [...new Set((this.selectedPayroll?.lines || []).map((line) => line.employeeName).filter(Boolean))];
      if (names.length) return names.join(', ');
      return this.headerConsultants
        .filter((consultant) => !this.isC2CType(consultant.consultantType))
        .map((consultant) => consultant.consultantName)
        .filter(Boolean)
        .join(', ');
    },
    payrollHeaderPeriod() {
      const payroll = this.selectedPayroll;
      if (!payroll) return '—';
      const start = payroll.payPeriodStart ? this.formatDateFn(payroll.payPeriodStart) : '';
      const end = payroll.payPeriodEnd ? this.formatDateFn(payroll.payPeriodEnd) : '';
      if (start && end) return `${start} – ${end}`;
      return start || end || '—';
    },
    payrollHeaderCheck() {
      const date = this.selectedPayroll?.checkDate || this.selectedPayroll?.payDate;
      return date ? this.formatDateFn(date) : '—';
    },
    payrollHeaderMoney() {
      const payroll = this.selectedPayroll || {};
      const money = (value) => {
        if (value === null || value === undefined || value === '') return '—';
        const amount = Number(value);
        return Number.isFinite(amount) ? this.formatCurrencyFn(amount) : '—';
      };
      return {
        gross: money(payroll.gross),
        net: money(payroll.net),
        deductions: money(payroll.deductions)
      };
    },
    payrollHeaderBankRecorded() {
      return !!this.selectedPayroll?.workflow?.reconciled;
    },
    payrollFlow() {
      const payroll = this.selectedPayroll;
      const flow = payroll?.workflow || {};
      const hasPayroll = !!payroll?.id;
      const timesheets = this.timesheetRows;
      const timesheetProgress = this.payrollTimesheetProgress();
      const steps = [
        {
          key: 'timesheet',
          label: 'Timesheet',
          done: timesheets.length > 0 && !timesheetProgress.partial,
          partial: timesheets.length > 0 && timesheetProgress.partial,
          at: timesheetProgress.at,
          note: timesheetProgress.status,
          title: timesheetProgress.status
        },
        {
          key: 'processed',
          label: flow.staged && !flow.processed ? 'Payroll Staged' : 'Payroll Processed',
          done: hasPayroll && (!!flow.processed || !!flow.staged),
          at: hasPayroll ? flow.createdAt : null,
          note: flow.staged && !flow.processed ? 'Staged' : ''
        },
        { key: 'paycheck', label: 'Paycheck', done: hasPayroll && !!flow.checkIssued, at: hasPayroll ? flow.checkAt : null },
        { key: 'posted', label: 'Posted', done: hasPayroll && !!flow.posted, at: hasPayroll ? flow.postedAt : null },
        { key: 'reconciled', label: 'Reconciled', done: hasPayroll && !!flow.reconciled, at: hasPayroll ? flow.reconciledAt : null }
      ];
      let current = -1;
      steps.forEach((step, index) => {
        if (step.done) current = index;
      });
      return steps.map((step, index) => ({
        key: step.key,
        label: step.label,
        when: (step.done || step.partial) ? (step.whenText || this.formatFlowWhen(step.at)) : '',
        note: (step.done || step.partial) ? (step.note || '') : '',
        title: step.title || '',
        state: step.partial
          ? 'partial'
          : (!step.done ? 'pending' : (index === current ? 'current' : 'done'))
      }));
    },
    payrollCards() {
      const payroll = this.selectedPayroll;
      const flow = payroll?.workflow || {};
      const date = (value) => (value ? this.formatDateFn(value) : '—');
      const money = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? this.formatCurrencyFn(n) : '—';
      };
      const lines = payroll?.journalEntries || [];
      const journalGroups = this.payrollJournalGroups(lines);
      const debit = lines.reduce((sum, line) => sum + (Number(line.debit) || 0), 0);
      const credit = lines.reduce((sum, line) => sum + (Number(line.credit) || 0), 0);
      const timesheets = this.timesheetRows;
      const timesheetGroups = this.timesheetGroups;
      const timesheetTotals = this.timesheetTotals;
      const firstTimesheet = timesheets[0] || null;
      const timesheetProgress = this.payrollTimesheetProgress();
      const timesheetHeadline = timesheetGroups.length === 1
        ? timesheetGroups[0].consultantName
        : (timesheetTotals.count ? `${timesheetTotals.count} timesheets` : 'No timesheets');
      const recorded = !!payroll?.id && (payroll.gross != null || payroll.net != null || !!flow.processed || !!flow.staged);
      return [
        {
          key: 'timesheet',
          n: 1,
          icon: 'bi bi-clock-history',
          title: 'Timesheet',
          headline: timesheetHeadline,
          rows: [
            { label: 'Type', value: this.summarizeTimesheetSources(timesheets) },
            { label: 'Period', value: firstTimesheet?.period || this.payrollHeaderPeriod },
            { label: 'Hours', value: timesheetTotals.count ? this.formatHours(timesheetTotals.hours) : '—' }
          ],
          status: timesheetProgress.status || 'Pending',
          tone: this.timesheetCardTone(timesheetProgress.status),
          action: 'timesheet',
          actionLabel: 'View Details'
        },
        {
          key: 'processed',
          n: 2,
          icon: 'bi bi-wallet2',
          title: 'Payroll',
          headline: recorded ? (payroll.staged ? 'Staged payroll' : `Payroll #${payroll.id}`) : '—',
          rows: [
            { label: 'Gross', value: recorded ? money(payroll.gross) : '—' },
            { label: 'Net', value: recorded ? money(payroll.net) : '—' },
            { label: 'Period', value: recorded ? this.payrollHeaderPeriod : '—' }
          ],
          status: recorded ? this.formatStatus(payroll.status) : 'Pending',
          tone: recorded ? (payroll.staged ? 'sent' : 'paid') : 'neutral',
          action: 'processed',
          actionLabel: 'View Details'
        },
        {
          key: 'paycheck',
          n: 3,
          icon: 'bi bi-cash-stack',
          title: 'Paycheck',
          headline: (flow.checkIssued || recorded) ? money(payroll.net) : '—',
          rows: [
            { label: 'Net pay', value: (flow.checkIssued || recorded) ? money(payroll.net) : '—' },
            { label: 'Check date', value: date(flow.checkAt) },
            { label: 'Deductions', value: (flow.checkIssued || recorded) ? money(payroll.deductions) : '—' }
          ],
          status: flow.checkIssued ? 'Issued' : 'Pending',
          tone: flow.checkIssued ? 'paid' : 'neutral',
          action: 'paycheck',
          actionLabel: 'View Details'
        },
        {
          key: 'gl',
          n: 4,
          icon: 'bi bi-journal-bookmark',
          title: 'General Ledger',
          headline: journalGroups.length > 1
            ? `${journalGroups.length} journal entries`
            : (journalGroups[0]?.reference || '—'),
          rows: journalGroups.length
            ? journalGroups.map((group) => ({
              label: this.payrollJournalLabel(group.title),
              value: group.reference,
              journalEntry: group.journalEntry
            }))
            : [
              { label: 'Debit', value: debit ? money(debit) : '—' },
              { label: 'Credit', value: credit ? money(credit) : '—' },
              { label: 'Journal', value: '—' }
            ],
          status: flow.posted ? 'Posted' : 'Pending',
          tone: flow.posted ? 'sent' : 'neutral',
          action: 'gl',
          actionLabel: 'View Details'
        },
        {
          key: 'bank',
          n: 5,
          icon: 'bi bi-bank',
          title: 'Bank',
          headline: flow.reconciled ? 'Bank statement' : '—',
          rows: [
            { label: 'Amount', value: flow.bank?.amount != null ? money(flow.bank.amount) : '—' },
            { label: 'Date', value: date(flow.bank?.date) }
          ],
          status: flow.reconciled ? 'Matched' : 'Pending',
          tone: flow.reconciled ? 'paid' : 'neutral',
          action: 'bank',
          actionLabel: 'View Details'
        },
        {
          key: 'reports',
          n: 6,
          icon: 'bi bi-bar-chart',
          title: 'Reports',
          headline: 'Payroll reports',
          list: ['Pay slips', 'Payroll summary', 'P&L'],
          rows: [],
          status: 'Available',
          tone: 'paid',
          action: 'reports',
          actionLabel: 'View Details'
        }
      ];
    },
    createdBill() {
      const selected = this.selectedBill;
      if (selected && !this.isDraftRecord(selected)) return selected;
      return this.relatedBills.find((bill) => !this.isDraftRecord(bill)) || null;
    },
    draftBillRecord() {
      const selected = this.selectedBill;
      if (selected && this.isDraftRecord(selected)) return selected;
      return this.relatedBills.find((bill) => this.isDraftRecord(bill)) || null;
    },
    timesheetRows() {
      const consultants = Array.isArray(this.invoiceDetails?.consultants)
        ? this.invoiceDetails.consultants
        : [];
      const rows = [];
      let rowSeq = 0;
      for (const consultant of consultants) {
        if (!consultant || typeof consultant !== 'object') continue;
        if (this.showPayroll) {
          const ids = this.payrollConsultantIds;
          if (ids.size) {
            if (consultant.id != null && !ids.has(String(consultant.id))) continue;
          } else if (this.isC2CType(consultant.consultantType)) {
            continue;
          }
        }
        for (const ts of consultant.timesheets || []) {
          const billLine = this.showBills ? this.billLineForTimesheet(ts.id) : null;
          const billRecords = this.selectedBill?.consultantRecords || [];
          if (this.showBills && billRecords.length && !billLine) continue;
          const [from, to] = String(ts.date || '').split(' to ');
          if (this.showPayroll && !this.timesheetOverlapsSelectedPayroll(from, to)) continue;
          const period = from && to
            ? `${this.formatDateFn(from)} – ${this.formatDateFn(to)}`
            : this.formatDateFn(from || to) || '—';
          const hours = Number(billLine?.hours ?? ts.hours);
          const invoiceRate = Number(ts.rate ?? ts.salesPrice);
          const billRate = Number(billLine?.hourlyRate ?? ts.purchasePrice);
          const rate = this.showBills ? billRate : invoiceRate;
          const billedTotal = Number(billLine?.total);
          const amountNum = this.showBills && Number.isFinite(billedTotal)
            ? billedTotal
            : (Number.isFinite(hours) && Number.isFinite(rate) ? hours * rate : NaN);
          rows.push({
            key: `ts-${consultant.id ?? 'c'}-${ts.id ?? rowSeq}-${rowSeq++}`,
            consultantKey: String(consultant.id ?? consultant.consultantName ?? ''),
            consultantName: consultant.consultantName || '—',
            consultantType: consultant.consultantType || '',
            vendorName: consultant.vendorName || '',
            fromDate: from || '',
            hoursNum: Number.isFinite(hours) ? hours : 0,
            amountNum: Number.isFinite(amountNum) ? amountNum : 0,
            period,
            sourceLabel: ts.hrTimesheetId ? 'HR Timesheet' : 'Manual',
            hours: Number.isFinite(hours) ? hours : '—',
            rate: Number.isFinite(rate) ? this.formatCurrencyFn(rate) : '—',
            amount: Number.isFinite(amountNum) ? this.formatCurrencyFn(amountNum) : '—',
            status: this.showPayroll
              ? (this.selectedPayroll?.status || ts.description || '—')
              : (this.showBills
                ? (billLine?.status || this.selectedBill?.status || '—')
                : (ts.description || '—')),
            attachments: Array.isArray(ts.attachments) ? ts.attachments : []
          });
        }
      }
      return rows;
    },
    timesheetGroups() {
      const groups = new Map();
      for (const row of this.timesheetRows) {
        if (!groups.has(row.consultantKey)) {
          groups.set(row.consultantKey, {
            key: row.consultantKey,
            consultantName: row.consultantName,
            consultantType: row.consultantType,
            vendorName: row.vendorName,
            rows: [],
            hours: 0,
            amount: 0
          });
        }
        const group = groups.get(row.consultantKey);
        group.rows.push(row);
        group.hours += row.hoursNum;
        group.amount += row.amountNum;
      }
      const list = Array.from(groups.values());
      list.forEach((group) => {
        group.rows.sort((a, b) => String(a.fromDate).localeCompare(String(b.fromDate)));
      });
      return list;
    },
    timesheetTotals() {
      return this.timesheetRows.reduce(
        (acc, row) => ({
          count: acc.count + 1,
          hours: acc.hours + row.hoursNum,
          amount: acc.amount + row.amountNum
        }),
        { count: 0, hours: 0, amount: 0 }
      );
    }
  },
  watch: {
    show(val) {
      if (!val) {
        this.openMenu = null;
        this.selectedWorkflowKey = null;
        this.showBills = false;
        this.selectedBillId = null;
        this.showPayroll = false;
        this.selectedPayrollId = null;
      }
    },
    detailsLoadKey: {
      immediate: true,
      handler(key) {
        if (key) {
          this.showBills = false;
          this.selectedBillId = null;
          this.showPayroll = false;
          this.selectedPayrollId = null;
          this.selectedWorkflowKey = 'timesheet';
          this.loadInvoiceDetails();
          return;
        }
        if (!this.billLoadKey) {
          this.showBills = false;
          this.selectedBillId = null;
          this.showPayroll = false;
          this.selectedPayrollId = null;
          this.selectedWorkflowKey = null;
          this.invoiceDetails = null;
          this.detailsError = '';
        }
      }
    },
    billLoadKey: {
      immediate: true,
      handler(key) {
        if (key) this.loadBillSummary();
      }
    }
  },
  methods: {
    billLineForTimesheet(timesheetId) {
      const records = this.selectedBill?.consultantRecords || [];
      return records.find((line) => Number(line.timesheetId) === Number(timesheetId)) || null;
    },
    summarizeTimesheetSources(rows) {
      const labels = [...new Set((rows || []).map((row) => row.sourceLabel).filter(Boolean))];
      if (!labels.length) return '—';
      return labels.join(', ');
    },
    timesheetCardTone(status) {
      const value = String(status || '').toLowerCase();
      if (!value || value === 'pending') return 'neutral';
      if (value.includes('partial')) return 'partial';
      if (value === 'time sheet created' || value === 'draft') return 'sent';
      return 'paid';
    },
    invoiceTimesheetProgress() {
      const flow = this.invoiceDetails?.workflow || {};
      const timesheets = this.timesheetRows;
      const statuses = [...new Set(
        timesheets
          .map((row) => String(row.status || '').trim())
          .filter((status) => status && status !== '—')
      )];
      const fromDates = timesheets.map((row) => row.fromDate).filter(Boolean).sort();
      let status = statuses[0] || '';
      if (flow.paymentReceived && !flow.paymentPartial) status = 'Payment Received';
      else if (flow.paymentPartial || statuses.some((item) => /partial/i.test(item))) status = 'Partial Payment Received';
      else if (flow.createdAt) status = 'Invoice Done';
      else if (!status && timesheets.length) status = 'Time Sheet Created';
      return {
        status,
        partial: status === 'Partial Payment Received',
        at: fromDates[0] || null
      };
    },
    dateOnly(value) {
      const text = String(value || '').trim();
      if (!text) return '';
      const iso = text.match(/^(\d{4}-\d{2}-\d{2})/);
      if (iso) return iso[1];
      const us = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (us) {
        return `${us[3]}-${String(us[1]).padStart(2, '0')}-${String(us[2]).padStart(2, '0')}`;
      }
      return '';
    },
    timesheetOverlapsSelectedPayroll(from, to) {
      const payroll = this.selectedPayroll;
      const start = this.dateOnly(payroll?.payPeriodStart);
      const end = this.dateOnly(payroll?.payPeriodEnd) || start;
      if (!start || !end) return true;
      const sheetFrom = this.dateOnly(from);
      const sheetTo = this.dateOnly(to) || sheetFrom;
      if (!sheetFrom || !sheetTo) return false;
      return sheetFrom <= end && start <= sheetTo;
    },
    payrollTimesheetProgress() {
      const flow = this.selectedPayroll?.workflow || {};
      const hasPayroll = !!this.selectedPayroll?.id;
      const fromDates = this.timesheetRows.map((row) => row.fromDate).filter(Boolean).sort();
      let status = '';
      if (hasPayroll && flow.checkIssued) status = 'Paycheck Issued';
      else if (hasPayroll && flow.processed) status = 'Payroll Processed';
      else if (this.timesheetRows.length) status = 'Time Sheet Created';
      return {
        status,
        partial: false,
        at: fromDates[0] || null
      };
    },
    billTimesheetProgress() {
      const flow = this.selectedBill?.workflow || {};
      const hasBill = !!this.selectedBill?.id;
      const created = hasBill && !this.isDraftRecord(this.selectedBill);
      const fromDates = this.timesheetRows.map((row) => row.fromDate).filter(Boolean).sort();
      let status = '';
      if (created && flow.paymentReceived && !flow.paymentPartial) status = 'Bill Paid';
      else if (created && flow.paymentPartial) status = 'Partial Payment';
      else if (created) status = 'Bill Created';
      else if (hasBill) status = 'Draft';
      else if (this.timesheetRows.length) status = 'Time Sheet Created';
      return {
        status,
        partial: status === 'Partial Payment',
        at: fromDates[0] || null
      };
    },
    isC2CType(type) {
      return String(type || '').trim().toUpperCase() === 'C2C';
    },
    isPayrollType(type) {
      const value = String(type || '').trim().toLowerCase().replace(/[\s_-]/g, '');
      return value === 'fulltime' || value === 'fulltimecompany' || value === '1099' || value === 'w2';
    },
    isDraftRecord(bill) {
      if (!bill?.id) return false;
      const status = String(bill.status || '').toLowerCase();
      if (status === 'draft') return true;
      return /^DRAFT-/i.test(String(bill.billNo || '').trim());
    },
    toggleBills() {
      this.showBills = !this.showBills;
      if (this.showBills) {
        this.showPayroll = false;
        this.selectedBillId = this.relatedBills[0]?.id || null;
        this.selectedWorkflowKey = 'timesheet';
      } else {
        this.selectedWorkflowKey = 'timesheet';
      }
    },
    payrollPeriodSortKey(payroll) {
      const raw = String(payroll?.payPeriodStart || payroll?.payPeriodEnd || '').trim();
      const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
      const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (us) return `${us[3]}-${us[1].padStart(2, '0')}-${us[2].padStart(2, '0')}`;
      const time = new Date(raw).getTime();
      return Number.isNaN(time) ? '' : new Date(time).toISOString().slice(0, 10);
    },
    payrollHeaderLabel(payroll) {
      if (!payroll) return 'Payroll';
      const start = payroll.payPeriodStart ? this.formatDateFn(payroll.payPeriodStart) : '';
      const end = payroll.payPeriodEnd ? this.formatDateFn(payroll.payPeriodEnd) : '';
      if (start && end) return `${start} – ${end}`;
      return start || end || (payroll.staged ? 'Staged payroll' : `Payroll #${payroll.id}`);
    },
    togglePayroll() {
      this.showPayroll = !this.showPayroll;
      if (this.showPayroll) {
        this.showBills = false;
        this.selectedPayrollId = this.relatedPayrolls[0]?.id || null;
        this.selectedWorkflowKey = 'timesheet';
      } else {
        this.selectedWorkflowKey = 'timesheet';
      }
    },
    selectPayroll(payroll) {
      this.selectedPayrollId = payroll?.id || null;
      this.selectedWorkflowKey = 'timesheet';
    },
    selectPayrollById(id) {
      const payroll = this.relatedPayrolls.find((item) => String(item.id) === String(id));
      this.selectPayroll(payroll);
      this.openMenu = null;
    },
    selectBill(bill) {
      this.selectedBillId = bill?.id || null;
      this.selectedWorkflowKey = 'timesheet';
    },
    journalEntryFromLine(line) {
      if (!line || line.journalEntryId == null) return null;
      return { id: line.journalEntryId, entryNo: line.reference || '' };
    },
    generalLedgerCardRows(lines, debit, credit, formatMoney) {
      const money = typeof formatMoney === 'function' ? formatMoney : (value) => String(value);
      const groups = this.payrollJournalGroups(lines);
      const rows = [
        { label: 'Debit', value: debit ? money(debit) : '—' },
        { label: 'Credit', value: credit ? money(credit) : '—' }
      ];
      if (!groups.length) {
        rows.push({ label: 'Journal', value: '—' });
        return rows;
      }
      groups.forEach((group, index) => {
        rows.push({
          label: groups.length > 1 ? `Journal ${index + 1}` : 'Journal',
          value: group.reference || '—',
          journalEntry: group.journalEntry
        });
      });
      return rows;
    },
    payrollJournalGroups(lines) {
      const groups = [];
      const seen = new Set();
      for (const line of lines || []) {
        const id = line?.journalEntryId;
        if (id == null || seen.has(id)) continue;
        seen.add(id);
        groups.push({
          id,
          reference: line.reference || '—',
          title: line.title || 'Journal entry',
          journalEntry: this.journalEntryFromLine(line)
        });
      }
      return groups;
    },
    payrollDetailRows(payroll) {
      const rows = [];
      const moneyOf = (value) => {
        const n = Number(value);
        return Number.isFinite(n) ? this.formatCurrencyFn(n) : '—';
      };
      const push = (name, item, amount) => {
        const n = Number(amount);
        if (!Number.isFinite(n) || Math.abs(n) < 0.005) return;
        rows.push({ name, item, amount: moneyOf(n) });
      };
      for (const line of payroll?.lines || []) {
        const name = line.employeeName || '—';
        push(name, 'Earnings', line.earnings);
        push(name, 'Reimbursement', line.reimbursement);
        for (const item of line.withholdings || []) push(name, item.label || 'Tax', item.amount);
        push(name, 'Benefits and other deductions', line.benefits);
        for (const item of line.employerTaxes || []) push(name, item.label || 'Employer tax', item.amount);
      }
      return rows;
    },
    payrollJournalLabel(title) {
      const text = String(title || '');
      if (/reimburse|travel/i.test(text)) return 'Reimbursement';
      if (/liabilit/i.test(text)) return 'Employer liabilities';
      if (/employer/i.test(text)) return 'Employer tax';
      if (/wage|gross/i.test(text)) return 'Wages';
      return text || 'Journal';
    },
    openJournalEntry(entry) {
      if (!entry || entry.id == null) return;
      this.journalEntryView = { id: entry.id, entryNo: entry.entryNo || '' };
    },
    openWorkflowCard(card) {
      this.selectedWorkflowKey = card.key;
      this.$nextTick(() => {
        const panel = this.$refs.workflowDetailPanel;
        if (panel && panel.scrollIntoView) panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    },
    viewCustomer() {
      const customerId = this.customer?.id;
      if (!customerId || !this.companyId) return;
      this.$emit('close');
      this.$router.push({
        name: 'CustomerCenter',
        params: { id: this.companyId },
        query: {
          customerId: String(customerId),
          customerName: this.customer?.customerName || '',
          view: 'invoices'
        }
      });
    },
    viewVendor() {
      const vendorId = this.billVendor?.id;
      if (!vendorId || !this.companyId) return;
      this.$emit('close');
      this.$router.push({
        name: 'VendorCenter',
        params: { id: this.companyId },
        query: { vendorId: String(vendorId) }
      });
    },
    async openAttachment(att) {
      const filePath = att?.filePath || att?.fileName;
      if (!filePath) return;
      const filename = String(filePath).split('/').pop();
      const query = this.companyId ? `?companyId=${encodeURIComponent(this.companyId)}` : '';
      try {
        const response = await ApiService.get(
          `/consultant/attachment/${encodeURIComponent(filename)}${query}`,
          { responseType: 'blob' }
        );
        const mimeType = response.headers?.['content-type'] || att.mimeType || 'application/pdf';
        const url = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
        window.open(url, '_blank');
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } catch (error) {
        logger.error('TransactionSummaryDialog: failed to open attachment', error);
        if (this.$toast) this.$toast.error('Failed to open attachment');
      }
    },
    formatFlowWhen(value) {
      if (!value) return '';
      const raw = String(value);
      const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(raw);
      const d = new Date(dateOnly ? `${raw}T00:00:00` : value);
      if (Number.isNaN(d.getTime())) return this.formatDateFn(value);
      const date = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      if (dateOnly || (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0)) return date;
      const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      return `${date} ${time}`;
    },
    formatHours(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return '—';
      return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    },
    statusTone(status) {
      const s = String(status || '').toLowerCase();
      if (s === 'paid') return 'paid';
      if (s === 'partial' || s === 'partially_paid') return 'partial';
      if (s === 'overdue') return 'overdue';
      if (s === 'sent' || s === 'open') return 'sent';
      return 'neutral';
    },
    formatStatus(status) {
      const s = String(status || '').trim();
      if (!s) return '—';
      return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    },
    async loadRecentInvoices(customerId, forInvoiceId) {
      this.recentInvoices = [];
      if (!customerId || !this.companyId) return;
      this.recentInvoicesLoading = true;
      try {
        const response = await ApiService.get('/customer/invoices', {
          params: { customerId, companyId: this.companyId, limit: 3, page: 1 }
        });
        if (String(this.invoiceId) !== String(forInvoiceId)) return;
        const data = response.data?.data;
        const rows = Array.isArray(data?.rows) ? data.rows : Array.isArray(data) ? data : [];
        this.recentInvoices = rows.slice(0, 3);
      } catch (error) {
        logger.warn('TransactionSummaryDialog: failed to load recent invoices', error);
      } finally {
        this.recentInvoicesLoading = false;
      }
    },
    openDocument(doc) {
      if (doc.kind === 'attachment') {
        const att = doc.attachment;
        const filename = String(att.filePath || att.fileName || '').split('/').pop();
        if (!filename) return;
        const params = new URLSearchParams({ source: att.source || 'invoice' });
        if (this.companyId) params.set('companyId', this.companyId);
        window.open(
          `${process.env.VUE_APP_API_URL}/customer/invoice-attachments/${encodeURIComponent(filename)}?${params}`,
          '_blank'
        );
      } else if (doc.kind === 'receipt') {
        const p = doc.payment;
        const inv = this.invoiceInfo;
        this.receiptPreviewGroup = {
          receivePaymentId: p.id,
          reference: p.reference,
          paymentDate: p.paymentDate,
          paymentMethod: p.paymentMethod,
          customerName: this.customer?.customerName || inv.customerName || '',
          totalReceived: p.amountReceived,
          invoices: [{
            invoiceId: inv.id,
            invoiceNo: inv.invoiceNo,
            invoiceDate: inv.date,
            invoiceStatus: inv.status
          }],
          allocationRows: (p.records || []).map((r) => ({ ...r, invoiceNo: inv.invoiceNo }))
        };
      } else if (doc.kind === 'journal') {
        this.journalEntryView = doc.journalEntry;
      }
    },
    async loadBillSummary() {
      this.invoiceDetails = null;
      this.recentInvoices = [];
      this.detailsError = '';
      this.showBills = false;
      this.showPayroll = false;
      const billId = this.billId;
      if (billId == null || billId === '') return;
      this.detailsLoading = true;
      this.selectedWorkflowKey = 'timesheet';
      try {
        const data = await ApiService.getBillSummary(billId, this.companyId);
        if (!this.show || String(this.billId) !== String(billId) || this.invoiceId) return;
        this.invoiceDetails = data || null;
        const bills = Array.isArray(data?.bills) ? data.bills : [];
        const match = bills.find((bill) => String(bill.id) === String(billId)) || bills[0] || null;
        this.selectedBillId = match?.id ?? null;
        this.showBills = !!match;
      } catch (error) {
        logger.error('TransactionSummaryDialog: failed to load bill details', error);
        this.detailsError = 'Could not load bill details and timesheets.';
      } finally {
        this.detailsLoading = false;
      }
    },
    async loadInvoiceDetails() {
      this.invoiceDetails = null;
      this.recentInvoices = [];
      this.detailsError = '';
      const invoiceId = this.invoiceId;
      if (invoiceId == null || invoiceId === '') return;
      this.detailsLoading = true;
      try {
        const data = await ApiService.getInvoiceDetails(this.companyId, invoiceId);
        if (String(this.invoiceId) !== String(invoiceId)) return;
        this.invoiceDetails = data || null;
        this.loadRecentInvoices(data?.invoice?.customer?.id, invoiceId);
      } catch (error) {
        logger.error('TransactionSummaryDialog: failed to load invoice details', error);
        this.detailsError = 'Could not load customer details and timesheets.';
      } finally {
        this.detailsLoading = false;
      }
    },
    toggleMenu(name) {
      this.openMenu = this.openMenu === name ? null : name;
    },
    selectAction(id) {
      this.openMenu = null;
      this.$emit('action', id);
    },
    printSummary() {
      this.openMenu = null;
      const el = this.$refs.summary;
      if (!el) return;
      const clone = el.cloneNode(true);
      clone.querySelectorAll('[data-print-hide]').forEach((n) => n.remove());
      const win = window.open('', '_blank', 'width=900,height=600');
      if (!win) return;
      const iconLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
        .map((l) => `<link rel="stylesheet" href="${l.href}">`)
        .join('');
      const safeTitle = String(this.title || 'Summary').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[c]);
      win.document.write(`<!doctype html><html><head><title>${safeTitle}</title>${iconLinks}
        <style>${PRINT_CSS}</style></head><body>${clone.outerHTML}</body></html>`);
      win.document.close();
      win.focus();
      setTimeout(() => {
        win.print();
        win.close();
      }, 300);
    }
  }
};

const PRINT_CSS = `
  body { font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif; color: #222; padding: 24px; line-height: 1.4; }
  .tsd-summary { display: flex; flex-direction: column; gap: 14px; }
  .tsd-summary__top { display: flex; align-items: center; gap: 6px 10px; flex-wrap: wrap; }
  .tsd-summary__title { font-size: 0.65rem; font-weight: 700; letter-spacing: 0.03em; }
  .tsd-summary__meta { display: flex; gap: 20px; flex-wrap: wrap; font-size: 0.65rem; color: #475569; }
  .tsd-summary__cards { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
  .tsd-summary__card { display: flex; align-items: center; gap: 10px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 10px; }
  .tsd-summary__label { font-size: 0.65rem; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase; color: #4338ca; }
  .tsd-summary__value { font-size: 0.65rem; font-weight: 400; color: #1e293b; }
  .consultant-preview-status, .rp-bank-linked-chip { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 0.65rem; font-weight: 600; background: #dcfce7; color: #15803d; }
`;
</script>

<style scoped>
.tsd-overlay {
  position: fixed;
  inset: 0;
  z-index: 2050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.45);
}

.tsd-dialog {
  width: min(1640px, calc(100vw - 24px));
  min-width: 360px;
  min-height: 200px;
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 1.5rem);
  overflow: auto;
  resize: both;
  box-sizing: border-box;
  padding: 0.6rem 0.85rem 0.85rem;
  background: #fff;
  font-family: 'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.4;
  border-radius: 12px;
  box-shadow: 0 20px 45px rgba(15, 23, 42, 0.25);
}

@media (max-width: 576px) {
  .tsd-dialog {
    min-width: 0;
    resize: none;
  }
}

.tsd-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.4rem;
}

.tsd-back {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
}

.tsd-back:hover {
  text-decoration: underline;
}

.tsd-summary__top {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.2rem 0.6rem;
}

.tsd-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
}

.tsd-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.65rem;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  background: #fff;
  color: #0f172a;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease;
}

.tsd-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.tsd-btn__caret {
  font-size: 0.7rem;
}

.tsd-menu-wrap {
  position: relative;
}

.tsd-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 5;
  min-width: 11rem;
  padding: 0.3rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.15);
}

.tsd-menu__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.45rem 0.6rem;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #334155;
  font-size: 0.82rem;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
}

.tsd-menu__item:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.tsd-dialog__close {
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 1rem;
  padding: 0.25rem 0.4rem;
  border-radius: 6px;
  cursor: pointer;
}

.tsd-dialog__close:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.tsd-summary {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.6rem 0.75rem;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.tsd-summary__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
}

.tsd-summary__slot {
  display: inline-flex;
  align-items: center;
}

.tsd-summary__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  flex-basis: 100%;
  gap: 0.35rem 1rem;
  font-size: 0.78rem;
  color: #475569;
}

.tsd-summary__meta-main {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem 1.25rem;
  min-width: 0;
}

.tsd-summary__consultants {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.3rem 0.45rem;
  margin-left: auto;
  min-width: 0;
}

.tsd-summary__clabel {
  color: #0f172a;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.2;
}

.tsd-summary__clabel::after {
  content: ':';
}

.tsd-summary__consultant {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  color: #334155;
  font-weight: 500;
}

.tsd-summary__cname {
  color: #0f172a;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.2;
}

.tsd-summary__ctype {
  padding: 0.02rem 0.35rem;
  border-radius: 999px;
  background: #e0ecff;
  color: #1d4ed8;
  font-size: 0.62rem;
  font-weight: 700;
}

.tsd-summary__cvendor {
  color: #475569;
  font-size: 0.75rem;
  font-weight: 600;
}

.tsd-summary__cvendor::before {
  content: 'Vendor:';
  margin-right: 0.28rem;
  color: #0f172a;
  font-size: 0.75rem;
  font-weight: 600;
}

.tsd-bills-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  margin-left: 0.15rem;
  padding: 0.16rem 0.5rem;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.2;
  cursor: pointer;
}

.tsd-bills-btn:hover {
  background: #dbeafe;
}

.tsd-payroll-period {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-left: auto;
}

.tsd-payroll-period__label {
  margin: 0;
  color: #475569;
  font-family: inherit;
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1.35;
  white-space: nowrap;
}

.tsd-payroll-period__tab {
  padding: 0.28rem 0.7rem;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  font-family: inherit;
  font-size: 0.65rem;
  font-weight: 600;
  line-height: 1.35;
  cursor: pointer;
}

.tsd-payroll-period__tab:hover {
  border-color: #93c5fd;
  color: #1d4ed8;
}

.tsd-payroll-period__tab--active {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
}

.tsd-bill-picks {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.45rem;
}

.tsd-bill-picks__btn {
  padding: 0.16rem 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #fff;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
}

.tsd-bill-picks__btn--active {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
}

.tsd-bills {
  margin-top: 0.15rem;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.tsd-bills__head {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.55rem;
  background: #f8fafc;
  border-bottom: 1px solid #eef2f7;
}

.tsd-bills__head h3 {
  margin: 0;
  color: #0f172a;
  font-size: 0.75rem;
  font-weight: 700;
}

.tsd-bills__head span {
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 700;
}

.tsd-bills__empty {
  margin: 0;
  padding: 0.45rem 0.55rem;
  color: #64748b;
  font-size: 0.75rem;
}

.tsd-bills__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}

.tsd-bills__table th,
.tsd-bills__table td {
  padding: 0.28rem 0.5rem;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
  white-space: nowrap;
}

.tsd-bills__table th {
  color: #94a3b8;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tsd-bills__table td {
  color: #0f172a;
}

.tsd-bills__table tbody tr:last-child td {
  border-bottom: none;
}

.tsd-summary__meta strong {
  color: #0f172a;
  font-weight: 600;
}

.tsd-summary__meta i {
  color: #64748b;
  margin-right: 0.2rem;
}

.tsd-summary__cards {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.45rem;
}

@media (max-width: 640px) {
  .tsd-summary__cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.tsd-summary__card {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.6rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.tsd-summary__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.8rem;
  height: 1.8rem;
  border-radius: 50%;
  font-size: 0.85rem;
}

.tsd-summary__icon--total { background: #e0ecff; color: #2563eb; }
.tsd-summary__icon--paid { background: #dcfce7; color: #16a34a; }
.tsd-summary__icon--balance { background: #ffe4e6; color: #e11d48; }
.tsd-summary__icon--status { background: #dcfce7; color: #15803d; }

.tsd-flow {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  margin-top: 0.6rem;
  padding: 0.65rem 0.4rem 0.55rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  list-style: none;
}

.tsd-flow--six {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.tsd-flow--seven {
  grid-template-columns: repeat(7, minmax(0, 1fr));
}

.tsd-flow__step {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  padding: 0 0.25rem;
  text-align: center;
}

.tsd-flow__track {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 1.15rem;
}

.tsd-flow__mark {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 50%;
  font-size: 0.62rem;
  line-height: 1;
}

.tsd-flow__step--done .tsd-flow__mark {
  background: #16a34a;
  color: #fff;
}

.tsd-flow__step--current .tsd-flow__mark {
  background: #2563eb;
  color: #fff;
  box-shadow: 0 0 0 3px #dbeafe;
}

.tsd-flow__step--partial .tsd-flow__mark {
  background: #f59e0b;
  color: #fff;
}

.tsd-flow__step--pending .tsd-flow__mark {
  background: #e2e8f0;
  color: #94a3b8;
}

.tsd-flow__label {
  max-width: 100%;
  margin-top: 0.28rem;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.tsd-flow__step--done .tsd-flow__label { color: #15803d; }
.tsd-flow__step--current .tsd-flow__label { color: #1d4ed8; }
.tsd-flow__step--partial .tsd-flow__label { color: #b45309; }
.tsd-flow__step--pending .tsd-flow__label { color: #94a3b8; }

.tsd-flow__when,
.tsd-flow__note {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.3;
}

.tsd-flow__when {
  margin-top: 0.1rem;
  font-size: 0.62rem;
  color: #64748b;
}

.tsd-flow__step--pending .tsd-flow__when { color: #94a3b8; }

.tsd-flow__note {
  min-height: 1.3em;
  font-size: 0.62rem;
  color: #64748b;
  white-space: normal;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.tsd-flow__arrow {
  position: absolute;
  left: calc(50% + 0.75rem);
  right: calc(-50% + 0.75rem);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #cbd5e1;
  font-size: 0.65rem;
}

.tsd-flow__arrow--done { color: #16a34a; }

.tsd-wfcards {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 0.55rem;
  padding: 0.55rem;
  background: #f3f7fb;
  border-radius: 10px;
}

.tsd-wfcards--bills {
  grid-template-columns: repeat(8, minmax(0, 1fr));
}

.tsd-wfcards--payroll {
  grid-template-columns: repeat(6, minmax(0, 1fr));
}

.tsd-wfcard {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
  padding: 0.55rem 0.6rem 0.5rem;
  background: #fff;
  border: 1px solid #e6eef6;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.tsd-wfcard--active {
  border-color: #93c5fd;
  box-shadow: 0 0 0 2px #dbeafe;
}

.tsd-wfdetail {
  margin-top: 0.35rem;
  padding: 0.45rem 0.55rem 0.5rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.tsd-wfdetail__head {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid #f1f5f9;
}

.tsd-wfdetail__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.45rem;
  height: 1.45rem;
  border-radius: 7px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.75rem;
}

.tsd-wfdetail__heading {
  flex: 1;
  min-width: 0;
}

.tsd-wfdetail__title-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.tsd-wfdetail__title {
  margin: 0;
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.2;
}

.tsd-wfdetail__headline {
  margin: 0;
  overflow: hidden;
  color: #64748b;
  font-size: 0.72rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tsd-wfdetail__close {
  margin-left: auto;
  padding: 0.2rem 0.55rem;
  border: 0;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
}

.tsd-wfdetail__close:hover {
  border-color: #bfdbfe;
  color: #1d4ed8;
}

.tsd-wfdetail__stats,
.tsd-wfdetail__fields {
  --tsd-label: 9rem;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: repeat(4, var(--tsd-label) minmax(0, 1fr));
  column-gap: 0.85rem;
  row-gap: 0;
  align-items: baseline;
  margin-top: 0.35rem;
  padding: 0.15rem 0.85rem;
  border: 1px solid transparent;
}

.tsd-wfdetail__stats {
  overflow: hidden;
  background: #f8fafc;
  border-radius: 8px;
}

.tsd-wfdetail__stat,
.tsd-wfdetail__field {
  display: grid;
  grid-template-columns: subgrid;
  grid-column: span 2;
  align-items: baseline;
  min-width: 0;
  padding: 0.28rem 0;
  background: transparent;
  border: 0;
  border-radius: 0;
}

.tsd-wfdetail__stat span,
.tsd-wfdetail__field dt {
  margin: 0;
  color: #94a3b8;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-align: left;
  white-space: nowrap;
}

.tsd-wfdetail__stat strong,
.tsd-wfdetail__field dd {
  margin: 0;
  overflow: hidden;
  min-width: 0;
  color: #0f172a;
  font-size: 0.78rem;
  font-weight: 600;
  line-height: 1.3;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tsd-wfdetail__note {
  margin: 0.4rem 0 0;
  color: #475569;
  font-size: 0.75rem;
  line-height: 1.35;
}

.tsd-wfdetail__fields {
  margin-top: 0.2rem;
  overflow: hidden;
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 8px;
}

.tsd-wfdetail__field {
  border-bottom: 1px solid #f1f5f9;
}

.tsd-wfdetail__field--wide {
  grid-column: 1 / -1;
  align-items: start;
}

.tsd-wfdetail__field--wide dd {
  grid-column: 2 / -1;
  overflow: visible;
  text-overflow: unset;
  white-space: normal;
  overflow-wrap: anywhere;
}

.tsd-wfdetail__table-wrap {
  margin-top: 0.35rem;
  padding: 0;
  overflow: hidden;
  background: #fff;
  border: 1px solid #eef2f7;
  border-radius: 8px;
}

.tsd-wfdetail__table-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.28rem 0.5rem 0.2rem;
}

.tsd-wfdetail__table-title {
  margin: 0;
  color: #0f172a;
  font-size: 0.75rem;
  font-weight: 700;
}

.tsd-wfdetail__table-head .tsd-link-btn {
  margin-left: auto;
}

.tsd-wfdetail__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.tsd-wfdetail__table th,
.tsd-wfdetail__table td {
  padding: 0.22rem 0.5rem;
  border-bottom: 1px solid #f1f5f9;
  text-align: left;
  white-space: nowrap;
}

.tsd-wfdetail__table th {
  background: #f8fafc;
  color: #94a3b8;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.tsd-wfdetail__table td {
  color: #0f172a;
}

.tsd-wfdetail__table tbody tr:last-child td {
  border-bottom: none;
}

.tsd-wfdetail__table tbody tr:hover td {
  background: #f8fbff;
}

.tsd-wfdetail__table th.tsd-num,
.tsd-wfdetail__table td.tsd-num {
  text-align: right;
}

.tsd-ts-group__cell {
  color: #64748b;
  font-weight: 600;
}

.tsd-ts-group__cell .tsd-ts-group__meta {
  margin-left: 0.45rem;
  font-weight: 400;
}

.tsd-ts-total td {
  background: #f8fafc;
  font-weight: 700;
}

@media (max-width: 1100px) {
  .tsd-wfdetail__stats,
  .tsd-wfdetail__fields {
    grid-template-columns: repeat(3, var(--tsd-label) minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .tsd-wfdetail__stats,
  .tsd-wfdetail__fields {
    grid-template-columns: repeat(2, var(--tsd-label) minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .tsd-wfdetail__stats,
  .tsd-wfdetail__fields {
    grid-template-columns: var(--tsd-label) minmax(0, 1fr);
  }
}

.tsd-wfdetail__docs,
.tsd-wfdetail__extra {
  margin-top: 0.35rem;
  padding: 0.28rem 0.45rem 0.35rem;
  background: #fff;
  border: 1px solid #e8eef6;
  border-radius: 8px;
}

.tsd-wfdetail__docs .tsd-doc-list,
.tsd-wfdetail__extra .tsd-link-btn {
  margin-top: 0.25rem;
}

.tsd-wfdetail__empty {
  margin: 0.7rem 0 0;
  padding: 0.7rem 0.8rem;
  background: #fff;
  border: 1px dashed #dbe3ee;
  border-radius: 10px;
  color: #64748b;
  font-size: 0.8rem;
}

.tsd-wfcard__arrow {
  position: absolute;
  top: 0.65rem;
  right: -1rem;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1rem;
  color: #64748b;
  font-size: 0.85rem;
  line-height: 1;
  pointer-events: none;
}

.tsd-wfcard__head {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  color: #0f172a;
}

.tsd-wfcard__num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 999px;
  background: #dbeafe;
  border: 1px solid #93c5fd;
  color: #1d4ed8;
  font-size: 0.68rem;
  font-weight: 700;
  line-height: 1;
}

.tsd-wfcard__head i {
  flex-shrink: 0;
  color: #2563eb;
  font-size: 0.9rem;
}

.tsd-wfcard__title {
  min-width: 0;
  overflow: hidden;
  color: #1d4ed8;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tsd-wfcard__headline {
  display: -webkit-box;
  min-height: 2.4em;
  margin: 0;
  overflow: hidden;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.25;
  color: #0f172a;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.tsd-wfcard__rows,
.tsd-wfcard__list {
  flex: 1;
  margin: 0;
}

.tsd-wfcard__rows {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.tsd-wfcard__row {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
  min-width: 0;
  font-size: 0.72rem;
  line-height: 1.4;
  white-space: nowrap;
}

.tsd-wfcard__row dt,
.tsd-wfcard__row dd {
  margin: 0;
}

.tsd-wfcard__row dt {
  flex: 0 0 auto;
  color: #64748b;
  font-weight: 400;
}

.tsd-wfcard__row dt::after {
  content: ':';
}

.tsd-wfcard__row dd {
  min-width: 0;
  overflow: visible;
  color: #0f172a;
  font-weight: 500;
}

.tsd-journal-link,
[data-ui-mode="compact"] .tsd-journal-link,
.tsd-dialog .tsd-wfcard__row .tsd-journal-link,
.tsd-dialog .tsd-wfdetail__field .tsd-journal-link,
.tsd-dialog .tsd-wfdetail__table .tsd-journal-link {
  max-width: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  color: #0f172a;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  font-style: inherit;
  letter-spacing: inherit;
  line-height: inherit;
  text-align: inherit;
  text-transform: inherit;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-decoration-skip-ink: none;
  text-underline-offset: 2px;
  cursor: pointer;
}

.tsd-wfcard__row .tsd-journal-link,
.tsd-dialog .tsd-wfcard__row dd .tsd-journal-link {
  display: inline;
  overflow: visible;
  white-space: nowrap;
  vertical-align: baseline;
}

.tsd-journal-link:hover,
.tsd-dialog .tsd-wfcard__row .tsd-journal-link:hover {
  color: #0f172a;
}

.tsd-wfcard__list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0;
  list-style: none;
  font-size: 0.7rem;
  line-height: 1.35;
  color: #0f172a;
}

.tsd-wfcard__list li {
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  overflow-wrap: anywhere;
}

.tsd-wfcard__list li::before {
  content: '';
  flex-shrink: 0;
  width: 0.3rem;
  height: 0.3rem;
  margin-top: 0.28rem;
  border-radius: 999px;
  background: #64748b;
}

.tsd-wfcard__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  margin-top: auto;
}

.tsd-wfcard__pill {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.12rem 0.45rem;
  font-size: 0.65rem;
  line-height: 1.2;
  white-space: normal;
}

.tsd-wfcard__foot {
  min-height: 1rem;
}

.tsd-wfcard__link {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.tsd-wfcard__link:hover {
  text-decoration: underline;
}

@media (max-width: 1280px) {
  .tsd-wfcards {
    grid-template-columns: repeat(7, minmax(11rem, 1fr));
    overflow-x: auto;
  }

  .tsd-wfcards--bills {
    grid-template-columns: repeat(8, minmax(11rem, 1fr));
  }

  .tsd-wfcards--payroll {
    grid-template-columns: repeat(6, minmax(11rem, 1fr));
  }
}

@media (max-width: 720px) {
  .tsd-flow {
    grid-template-columns: repeat(5, minmax(7.5rem, 1fr));
    overflow-x: auto;
  }

  .tsd-flow--six {
    grid-template-columns: repeat(6, minmax(7.5rem, 1fr));
  }

  .tsd-flow--seven {
    grid-template-columns: repeat(7, minmax(7.5rem, 1fr));
  }
}

:slotted(.consultant-preview-status) {
  border: none !important;
  box-shadow: none !important;
}

.tsd-section {
  margin-top: 0.6rem;
  padding: 0.55rem 0.75rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.tsd-sections {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
  margin-top: 0.6rem;
  align-items: stretch;
}

.tsd-sections > .tsd-section {
  display: flex;
  flex-direction: column;
  margin-top: 0;
  min-width: 0;
}

.tsd-sections > .tsd-section > .tsd-link-btn {
  align-self: flex-start;
  margin-top: auto;
  padding-top: 0.45rem;
}

.tsd-sections > .tsd-section--full {
  grid-column: 1 / -1;
}

@media (max-width: 640px) {
  .tsd-sections {
    grid-template-columns: minmax(0, 1fr);
  }
}

.tsd-section--muted {
  color: #64748b;
  font-size: 0.82rem;
}

.tsd-section--error {
  color: #b91c1c;
  font-size: 0.82rem;
  background: #fef2f2;
  border-color: #fecaca;
}

.tsd-section__title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0 0 0.45rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #0f172a;
}

.tsd-section__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 5px;
  background: #1e40af;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
}

.tsd-kv {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 0.3rem 0.9rem;
  align-items: center;
  margin: 0;
  font-size: 0.75rem;
}

.tsd-kv dt {
  font-weight: 400;
  color: #475569;
  white-space: nowrap;
}

.tsd-kv dd {
  margin: 0;
  color: #0f172a;
  min-width: 0;
  overflow-wrap: anywhere;
}

.tsd-kv__customer {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.tsd-kv__customer i {
  color: #1e40af;
  font-size: 0.85rem;
}

.tsd-kv__customer:hover {
  text-decoration: underline;
}

.tsd-kv__money {
  font-weight: 700;
}

.tsd-kv__money--due {
  color: #dc2626 !important;
}

.tsd-doc-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tsd-recent {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 5.5rem;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.8rem;
}

.tsd-recent .tsd-pill {
  justify-self: end;
}

.tsd-recent__no {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: #1d4ed8;
  font-weight: 500;
  min-width: 0;
  white-space: nowrap;
}

.tsd-recent__current {
  flex-shrink: 0;
  padding: 0 0.4rem;
  border-radius: 4px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.68rem;
  font-weight: 600;
}

.tsd-recent__amount {
  color: #0f172a;
  text-align: right;
  white-space: nowrap;
}

.tsd-pill {
  flex-shrink: 0;
  justify-self: start;
  padding: 0.08rem 0.45rem;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
}

.tsd-pill--paid { background: #dcfce7; color: #15803d; }
.tsd-pill--partial { background: #d1fae5; color: #047857; }
.tsd-pill--overdue { background: #fee2e2; color: #b91c1c; }
.tsd-pill--sent { background: #dbeafe; color: #1d4ed8; }
.tsd-pill--neutral { background: #f1f5f9; color: #475569; }

.tsd-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.15rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 0.82rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
}

.tsd-link-btn:hover {
  text-decoration: underline;
}

.tsd-section > .tsd-link-btn {
  margin-top: 0.75rem;
}

.tsd-doc,
[data-ui-mode="compact"] .tsd-dialog .tsd-doc {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.22rem 0.5rem;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.65rem;
  font-weight: 400;
  line-height: 1.35;
  color: #0f172a;
}

.tsd-doc:last-child {
  border-bottom: none;
}

.tsd-doc__icon,
[data-ui-mode="compact"] .tsd-dialog .tsd-doc__icon {
  flex-shrink: 0;
  width: 0.9rem;
  text-align: center;
  font-size: 0.75rem;
  line-height: 1;
}

.tsd-doc__icon--pdf { color: #dc2626; }
.tsd-doc__icon--file { color: #64748b; }
.tsd-doc__icon--receipt { color: #2563eb; }
.tsd-doc__icon--journal { color: #4f46e5; }

.tsd-doc__label,
[data-ui-mode="compact"] .tsd-dialog .tsd-doc__label {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  font-size: 0.65rem;
  font-weight: 400;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.tsd-doc__sub,
[data-ui-mode="compact"] .tsd-dialog .tsd-doc__sub {
  font-size: 0.65rem;
  font-weight: 400;
  line-height: 1.35;
  color: #64748b;
  overflow-wrap: anywhere;
}

.tsd-doc .tsd-link-btn,
[data-ui-mode="compact"] .tsd-dialog .tsd-doc .tsd-link-btn {
  margin: 0 0 0 auto;
  padding: 0;
  font-size: 0.65rem;
  font-weight: 400;
  line-height: 1.35;
}

.tsd-contact {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tsd-contact__row {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.75rem;
  color: #334155;
  min-width: 0;
}

.tsd-contact__icon {
  flex-shrink: 0;
  width: 1.25rem;
  text-align: center;
  font-size: 0.85rem;
  color: #64748b;
}

.tsd-contact__row--name .tsd-contact__icon {
  color: #1e40af;
}

.tsd-contact__name {
  min-width: 0;
  font-weight: 600;
  color: #0f172a;
  overflow-wrap: anywhere;
}

.tsd-contact__text {
  color: #334155;
  text-decoration: none;
  overflow-wrap: anywhere;
}

a.tsd-contact__text:hover {
  color: #2563eb;
}

.tsd-section__count {
  padding: 0.05rem 0.5rem;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-size: 0.72rem;
  font-weight: 600;
}

.tsd-details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: 0.65rem 1.25rem;
}

.tsd-details-grid__item {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.tsd-details-grid__item--wide {
  grid-column: 1 / -1;
}

.tsd-details-grid__label {
  font-size: 0.7rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.tsd-details-grid__value {
  font-size: 0.85rem;
  color: #0f172a;
  overflow-wrap: anywhere;
}

.tsd-details-grid__value a {
  color: #2563eb;
  text-decoration: none;
}

.tsd-ts {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.tsd-ts-group__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 42rem;
  margin-top: 0.35rem;
  padding: 0.25rem 0.5rem 0.1rem;
}

.tsd-ts-group__name {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #0f172a;
}

.tsd-ts-group__name i {
  color: #1e40af;
}

.tsd-ts-group__type {
  padding: 0.05rem 0.35rem;
  border-radius: 999px;
  background: #e0ecff;
  color: #1d4ed8;
  font-size: 0.65rem;
  font-weight: 700;
}

.tsd-ts-group__vendor {
  color: #475569;
  font-size: 0.68rem;
  font-weight: 500;
}

.tsd-ts-group__meta {
  font-size: 0.68rem;
  color: #64748b;
}

.tsd-ts-grid {
  overflow-x: auto;
}

.tsd-ts-grid__row {
  display: grid;
  grid-template-columns:
    1.5rem
    minmax(8.5rem, 1.2fr)
    minmax(6.5rem, 0.9fr)
    minmax(3.75rem, 0.7fr)
    minmax(5.75rem, 1fr)
    minmax(6.75rem, 1fr)
    minmax(6.5rem, 1fr)
    minmax(3.5rem, 0.6fr);
  align-items: center;
  column-gap: 0.6rem;
  min-width: 42rem;
  padding: 0.28rem 0.5rem;
  font-size: 0.75rem;
  color: #0f172a;
  box-sizing: border-box;
}

.tsd-ts-grid__row > span {
  min-width: 0;
}

.tsd-ts-grid__center {
  text-align: center;
  justify-content: center;
}

.tsd-ts-grid__row--head {
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  font-size: 0.68rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.tsd-ts-grid__row--subtotal {
  font-weight: 700;
  color: #334155;
}

.tsd-ts-grid__num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.tsd-ts-grid__idx {
  color: #94a3b8;
}

.tsd-ts-grid__strong {
  font-weight: 600;
}

.tsd-ts-grid__files {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  color: #94a3b8;
}

.tsd-file-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: #eff6ff;
  color: #1d4ed8;
  cursor: pointer;
}

.tsd-file-btn:hover {
  background: #dbeafe;
}

.tsd-ts-grid__row--grand {
  margin-top: 0.3rem;
  background: #eff6ff;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  color: #1e3a8a;
}

.tsd-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.tsd-attachment {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.2rem 0.55rem;
  border: none;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.78rem;
  cursor: pointer;
}

.tsd-attachment:hover {
  background: #dbeafe;
}

.tsd-dialog .tsd-summary__label,
.tsd-dialog .tsd-wfdetail__stat span,
.tsd-dialog .tsd-wfdetail__field dt,
.tsd-dialog .tsd-details-grid__label,
.tsd-dialog .tsd-wfdetail__table th,
.tsd-dialog .tsd-bills__table th,
.tsd-dialog .tsd-ts-grid__row--head {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  line-height: 1.35;
  text-transform: uppercase;
}

.tsd-dialog .tsd-summary__title,
.tsd-dialog .tsd-summary__value,
.tsd-dialog .tsd-summary__cname,
.tsd-dialog .tsd-summary__meta,
.tsd-dialog .tsd-payroll-period__label,
.tsd-dialog .tsd-payroll-period__tab,
.tsd-dialog .tsd-summary__clabel,
.tsd-dialog .tsd-summary__cvendor,
.tsd-dialog .tsd-summary__cvendor::before,
.tsd-dialog .tsd-back,
.tsd-dialog .tsd-btn,
.tsd-dialog .tsd-menu__item,
.tsd-dialog .tsd-bills-btn,
.tsd-dialog .tsd-bill-picks__btn,
.tsd-dialog .tsd-flow__label,
.tsd-dialog .tsd-flow__when,
.tsd-dialog .tsd-flow__note,
.tsd-dialog .tsd-wfcard__title,
.tsd-dialog .tsd-wfcard__headline,
.tsd-dialog .tsd-wfcard__row,
.tsd-dialog .tsd-wfcard__row dt,
.tsd-dialog .tsd-wfcard__row dd,
.tsd-dialog .tsd-wfcard__list,
.tsd-dialog .tsd-wfcard__link,
.tsd-dialog .tsd-wfdetail__title,
.tsd-dialog .tsd-wfdetail__headline,
.tsd-dialog .tsd-wfdetail__note,
.tsd-dialog .tsd-wfdetail__close,
.tsd-dialog .tsd-wfdetail__empty,
.tsd-dialog .tsd-wfdetail__stat strong,
.tsd-dialog .tsd-wfdetail__field dd,
.tsd-dialog .tsd-wfdetail__table,
.tsd-dialog .tsd-wfdetail__table td,
.tsd-dialog .tsd-wfdetail__table-title,
.tsd-dialog .tsd-section__title,
.tsd-dialog .tsd-section--muted,
.tsd-dialog .tsd-section--error,
.tsd-dialog .tsd-details-grid__value,
.tsd-dialog .tsd-kv,
.tsd-dialog .tsd-kv__customer,
.tsd-dialog .tsd-link-btn,
.tsd-dialog .tsd-doc,
.tsd-dialog .tsd-doc__sub,
.tsd-dialog .tsd-contact__row,
.tsd-dialog .tsd-recent,
.tsd-dialog .tsd-bills__table,
.tsd-dialog .tsd-bills__table td,
.tsd-dialog .tsd-bills__empty,
.tsd-dialog .tsd-bills__head h3,
.tsd-dialog .tsd-bills__head span,
.tsd-dialog .tsd-ts-grid__row,
.tsd-dialog .tsd-ts-grid__row--grand,
.tsd-dialog .tsd-ts-group__name,
.tsd-dialog .tsd-ts-group__meta,
.tsd-dialog .tsd-ts-group__vendor,
.tsd-dialog .tsd-attachment,
.tsd-dialog .tsd-pill,
.tsd-dialog .tsd-wfcard__pill,
.tsd-dialog .tsd-summary__ctype,
.tsd-dialog .tsd-ts-group__type,
.tsd-dialog .tsd-section__count,
.tsd-dialog .tsd-recent__current {
  font-size: 0.65rem;
  line-height: 1.35;
}
</style>
